import { fetchRoutes, fetchThreeCategoryPools, type RawRoute, type RouteExclude, type ScenicPoint, type TravelMode } from "@/api/routing";
import { findPleasantWaypoints, localBackroadSequence, type PleasantWaypoint } from "@/api/pleasantWaypoints";
import { fetchOsmFeaturesAndPoints, fetchOsmEnvironment, fetchOsmEnvironmentForRoutes, fetchOsmEnvironmentForNewRoutes, mergeOsmEnvironments, fetchThroughParkNames, type OsmEnvironment } from "@/api/osm";
import { searchPoisInBbox } from "@/api/places";
import { searchPlacesAlongRoute } from "@/api/googlePlaces";
import { searchPlacesAlongRouteOverpass } from "@/api/overpassPlaces";
import { fetchTagsInBbox, fetchSignalsAlongRoute, type CommunityTag } from "@/api/community";
import { fetchBaseRoutesWithFallback } from "@/api/baseRoutes";
import { setOverpassDeadline } from "@/api/osm";

/**
 * HARD READY CEILING for one search (Morgan: "we can't wait 45 seconds" —
 * measured READY at 45590ms in her console log).
 *
 * Hoisted to module scope Aug 22 so the deadline can be armed at T0. It is now
 * the authoritative product constraint for the whole run: no dependency of a
 * search may hold a required window longer than the time the search has left.
 * Read by setOverpassDeadline at the top of runRouteGeneration and by the
 * enrichment budget far below — one number, two consumers, no drift.
 */
const READY_CEILING_MS = 25_000;
import { shadowEnabled, runProductionShadow } from "@/utils/productionShadow";
import { enableAttrition, beginAttrition, endAttrition, clearTraces,
         roadsOfSteps } from "@/utils/attrition";
import {
  BUFFER_BY_MODE, bbox, classifyRoutes, classifyThreeCards, scoreRoute,
  summarizeFeatures, communityScore,
  type ClassifiedRoute, type Preference, type ScoredRoute,
} from "@/utils/scoring";
import { samplePolylineMax, haversine, nearestDistance, nearestPointOnPolyline, cumulativeDistances, type LngLat } from "@/utils/polyline";
import { trimOutAndBackSpurs } from "@/utils/routeShape";
import { townsAlongCorridor, coastalTownsAlongCorridor, isCoastalTownTrip } from "@/utils/charmingTowns";
// (#174) Loop validators applied to anchor-routed candidates BEFORE
// they enter the pool. Per Morgan: routing engines don't return loopy
// routes; we created them by feeding the engines anchors that forced
// detours. The fix is to validate at the source, not filter downstream.
import { hasNearReturn, hasNeighborhoodDetour, hasBacktrackTowardDestination, pleasantBudgetSeconds, selfOverlapFraction, contiguousRetraceMeters } from "@/utils/scoring";
// (#175) Debug overlay — tag routes with source as they're created so
// the overlay can show "Pleasant came from valhalla-balanced" / "Adventure
// came from anchor:Patriots Park". Delete when debugging is done.
import { tagRouteSource, tagRoutesSource, getRouteSource, type RouteDiagnostics } from "@/lib/routeDiagnostics";
import { beginSearch, recordSearch, type EnrichmentVerdict } from "@/lib/searchTelemetry";
import { routeEtaFragment } from "@/utils/routeDebugId";
import { pchWaypointsForCorridor } from "@/utils/pchWaypoints";
import { scenicRoadsForCorridor, scenicRoadsAlongCorridor, curatedRoadsTravelled } from "@/utils/scenicRoads";
import {
  TOLERANCE_MULTIPLIER,
  type UserProfile, type PleasantnessProfile,
} from "@/utils/profile";
import type { TripPoint } from "@/components/TripSearch";
import type { ScenicScoringResult } from "@/api/scenicScoring";
import { fmtDistance } from "@/utils/units";
import { toast } from "sonner";

type AppliedDetour = {
  summary: string;
  waypoints: { label: string; reason?: string; coord?: LngLat }[];
} | undefined;

/** Named scenic points (parks, water, historic places) from OSM, filtered to
 *  those that genuinely lie close to the route line and deduped by name.
 *  Attached to the route so scenic narration can speak of the route's own
 *  features — a river, a park — not only of Google POIs. */
export function buildScenicPoints(
  route: RawRoute,
  osmPoints: {
    parks: { lng: number; lat: number; name?: string }[];
    water: { lng: number; lat: number; name?: string }[];
    historic: { lng: number; lat: number; name?: string }[];
  },
): ScenicPoint[] {
  const NEAR_M = 120; // a feature must be within 120 m of the route line
  const out: ScenicPoint[] = [];
  const seen = new Set<string>();
  const groups: Array<[ScenicPoint["kind"], { lng: number; lat: number; name?: string }[]]> = [
    ["water", osmPoints.water],
    ["park", osmPoints.parks],
    ["historic", osmPoints.historic],
  ];
  // Built once (PLE-6): without it nearestPointOnPolyline rebuilds the
  // route's cumulative distances for every point. `distance` never reads it.
  const routeCum = cumulativeDistances(route.coordinates);
  for (const [kind, pts] of groups) {
    for (const p of pts) {
      const name = (p.name ?? "").trim();
      if (!name) continue; // an unnamed pond is nothing to narrate
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      const snap = nearestPointOnPolyline([p.lng, p.lat], route.coordinates, routeCum);
      if (snap.distance > NEAR_M) continue;
      seen.add(key);
      out.push({ kind, name, lng: p.lng, lat: p.lat });
    }
  }
  return out;
}

/** Inputs passed to a single route-generation invocation. */
export interface GenerateRoutesArgs {
  startCoords: LngLat;
  dest: TripPoint;
  /** Explicit user-picked waypoints (e.g. drive-by detours). */
  waypoints?: LngLat[];
  /** Pre-built explainer payload for user-added detours. */
  detourMeta?: { summary: string; waypoints: { label: string; reason?: string }[] };
  /** Bypass stale `mode` closure value (e.g. mid-flight mode switch). */
  modeOverride?: TravelMode;
  /** Set when called from "Try a different route". */
  tryAgainOpts?: { attempt: number; exclude: LngLat[] };
  /** Google's FULL route for the same trip (when available) — joins the
   *  candidate pool as a real contender (Morgan, July 17: the natural
   *  riverside fastest was display-only and could never win Pleasant). */
  googleRoute?: { coordinates: LngLat[]; durationS: number; distanceM?: number };
  /** Google Maps polyline for the same trip (when available). Passed
   *  to classifyRoutes so the Pleasant pool can actively drop
   *  candidates that overlap Google ≥ 35%. Optional — when
   *  unset, the Google-overlap filter is a no-op (#92). */
  googleCoords?: LngLat[];
}

/**
 * Lifecycle of a single route-generation invocation.
 *  - `idle`      → nothing in flight, no result
 *  - `loading`   → request started, no usable result yet
 *  - `partial`   → cheap-path scored routes are visible; enrichment running
 *  - `ready`     → fully enriched results applied
 *  - `error`     → request failed (see `error` string for details)
 *  - `cancelled` → request was superseded by a newer one or explicitly cancelled
 */
export type RouteGenStatus = "idle" | "loading" | "partial" | "ready" | "error" | "cancelled";

/** State + refs the route-generation pipeline needs from the host component. */
export interface RouteGenerationContext {
  // Reads
  mode: TravelMode;
  profile: UserProfile;
  prioritizeCommunity: boolean;
  // Refs (stable identity, mutable contents)
  generationIdRef: { current: number };
  triedWaypointsRef: { current: LngLat[] };
  shownPleasantRef: { current: LngLat[][] };
  // Setters
  setLoading: (v: boolean) => void;
  setAppliedDetour: (v: AppliedDetour) => void;
  setFastestPleasantBaseline: (v: number | undefined) => void;
  setScored: (v: ClassifiedRoute[] | ((prev: ClassifiedRoute[]) => ClassifiedRoute[])) => void;
  setSelectedClass: (v: "fastest" | "scenic" | "pleasant") => void;
  setTags: (v: CommunityTag[]) => void;
  /** (#175) Optional diagnostic-overlay setter. Populated by the host
   *  component when debug mode is on; left undefined in production. */
  setRouteDiagnostics?: (v: RouteDiagnostics | null) => void;
  /** Lifecycle status + accompanying error message (only populated for "error"). */
  setStatus: (status: RouteGenStatus, error?: string | null) => void;
}

/** Helpers — pure, kept alongside the generator. */
const PROFILE_ROTATION: PleasantnessProfile[] = ["nature", "water", "historic", "quiet", "lively"];

/** (#144 / #145) Fetch Direct + Pleasant + Scenic candidate pools in
 *  parallel, then merge into one deduped list for downstream scoring.
 *  Dedup uses a coarse polyline signature (every 10th point at 4-decimal
 *  precision) so near-identical alternatives from different pools — e.g.
 *  the no-filter Direct call AND the exclude=motorway Pleasant call both
 *  returning the same Bedford Rd path — collapse to a single candidate
 *  instead of inflating the pool with copies. We intentionally pass
 *  EMPTY scenic anchors here so the cheap path doesn't block on an
 *  Overpass lookup; the background enrichment pass later refreshes with
 *  real anchors.
 *
 *  Real-world assumption: Mapbox returns genuinely different alternatives
 *  when asked with different exclude lists, even between the no-filter
 *  Direct query and the Pleasant motorway-excluded query. If that turns
 *  out to be false (Direct and Pleasant return identical alternatives),
 *  the dedup will collapse them and we'll see fewer candidates than
 *  expected — fine, we'd then add a stronger differentiator. */
async function fetchThreePoolsAndDedup(
  startCoords: LngLat,
  destCoords: LngLat,
): Promise<RawRoute[]> {
  // Mapbox-only pool: ONE parallel call set (no-filter Direct + motorway-
  // excluded Pleasant + scenic), all fast (~1s). Valhalla used to run here too
  // for graph-level backroads, but its ~20s round-trip was the single biggest
  // cause of frozen-feeling route generation, and Mapbox's motorway-excluded
  // pool already surfaces residential alternatives. Dropped (Morgan, speed
  // pass): the first paint must be one Mapbox round-trip.
  const pools = await fetchThreeCategoryPools(
    startCoords,
    destCoords,
    "driving",
    [], // scenic anchors filled in by the background enrichment pass
  );
  tagRoutesSource(pools.direct, "mapbox-direct");
  tagRoutesSource(pools.pleasant, "mapbox-pleasant");
  tagRoutesSource(pools.scenic, "mapbox-scenic");

  const signature = (coords: LngLat[]): string =>
    coords
      .filter((_, i) => i % 10 === 0)
      .map(([lng, lat]) => `${lng.toFixed(4)},${lat.toFixed(4)}`)
      .join("|");
  const seen = new Set<string>();
  const merged: RawRoute[] = [];
  for (const r of [...pools.direct, ...pools.pleasant, ...pools.scenic]) {
    const sig = signature(r.coordinates);
    if (sig.length === 0 || seen.has(sig)) continue;
    seen.add(sig);
    merged.push(r);
  }
  return merged;
}

/** (#174) Filter scenic anchor waypoints to those near the natural
 *  corridor. An anchor more than `corridorBufferM` meters from any point
 *  on the natural Direct route would force the engine to make a major
 *  detour to reach it — that detour is what creates visible teardrop
 *  loops and "drive a block and come back" patterns. By rejecting
 *  far-off-corridor anchors at the source, we keep the routing engine
 *  honest. Anchors that ARE near the corridor produce smooth detours
 *  the engine handles naturally.
 *
 *  `corridor` is preferentially the Google polyline (the canonical
 *  Direct). If unavailable, we fall back to the engine's fastest base
 *  alternative. Both come in as `LngLat[]`. */
function filterAnchorsToCorridor(
  anchors: PleasantWaypoint[],
  corridor: LngLat[] | undefined,
  corridorBufferM: number = 500,
): PleasantWaypoint[] {
  if (!corridor || corridor.length < 2) return anchors;
  return anchors.filter((wp) => nearestDistance(wp.coord, corridor) <= corridorBufferM);
}

/** (#174) Validate a routing-engine result against the loop detectors
 *  BEFORE it enters the candidate pool. If an anchor produced a route
 *  that loops or dead-ends, drop it at the source rather than letting
 *  it survive into the pool and rely on downstream filters (which have
 *  fallbacks that can re-surface the loopy answer). The validators are
 *  the same ones classifyRoutes uses, kept consistent so this filter
 *  is strictly stricter than the downstream pool filter.
 *
 *  `destCoord` is required for the backtrack-toward-destination filter. */
function isCleanAnchorRoute(route: RawRoute, destCoord: LngLat): boolean {
  if (hasNearReturn(route.coordinates)) return false;
  if (hasNeighborhoodDetour(route.coordinates)) return false;
  if (hasBacktrackTowardDestination(route.coordinates, destCoord)) return false;
  return true;
}

const routePointAt = (coords: LngLat[], t: number): LngLat => {
  if (coords.length === 0) return [0, 0];
  const idx = Math.max(0, Math.min(coords.length - 1, Math.round(t * (coords.length - 1))));
  return coords[idx];
};
const bearingBetween = (a: LngLat, b: LngLat): number => {
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return Math.atan2(y, x);
};
const offsetFrom = (point: LngLat, bearing: number, offsetM: number): LngLat => {
  const perp = bearing + (offsetM > 0 ? Math.PI / 2 : -Math.PI / 2);
  const dist = Math.abs(offsetM);
  const latRad = (point[1] * Math.PI) / 180;
  const dLat = (Math.cos(perp) * dist) / 111320;
  const dLng = (Math.sin(perp) * dist) / (111320 * Math.max(0.2, Math.cos(latRad)));
  return [point[0] + dLng, point[1] + dLat];
};
const routeOffsetChain = (coords: LngLat[], fractions: number[], offsetM: number): LngLat[] =>
  fractions.map((t) => {
    const here = routePointAt(coords, t);
    const before = routePointAt(coords, Math.max(0, t - 0.03));
    const after = routePointAt(coords, Math.min(1, t + 0.03));
    return offsetFrom(here, bearingBetween(before, after), offsetM);
  });

/**
 * Practical distance ceilings for active travel. Beyond these, Mapbox's
 * walking/cycling profiles return nothing and the user just sees a blank
 * screen or a generic "couldn't load routes" — the worst kind of dead end.
 * We catch it BEFORE firing the doomed request and hand back mode-specific
 * guidance instead. Generous on purpose: a real all-day walk or a long bike
 * tour still goes through; only clearly-wrong-mode trips (e.g. a 190-mi
 * "walk" from Rhinebeck to Boston) are stopped.
 */
const ACTIVE_TRAVEL_MAX_MI: Partial<Record<TravelMode, number>> = {
  walking: 60,
  biking: 250,
};

/** Max coordinates a route keeps for the DISPLAY/scoring/overview phase. Mapbox
 *  returns full-resolution geometry — ~35,000 points on a coast-to-coast trip —
 *  which froze scoring and React render for ~11s each (measured). 3,000 points
 *  is visually identical at any zoom you'd view a long route at, and keeps every
 *  O(n)/O(n·m) geometry op trivially fast. */
export const MAX_DISPLAY_POINTS = 3000;

/** Downsample a route's geometry for the selection/scoring/overview phase, in
 *  place, preserving the full-resolution line on `navCoordinates` for turn-by-turn
 *  navigation (which needs meter-level precision near maneuvers). MUTATES the
 *  route object so its WeakMap source tag (getRouteSource) survives — returning a
 *  new object would drop the tag and break classification. No-op for routes that
 *  are already short or already capped. */
export function capRouteGeometry(r: RawRoute, maxPoints = MAX_DISPLAY_POINTS): void {
  if (r.navCoordinates || r.coordinates.length <= maxPoints) return;
  r.navCoordinates = r.coordinates;
  r.coordinates = samplePolylineMax(r.coordinates, maxPoints);
  // `congestion` carries one entry per ORIGINAL coordinate-pair, so it no longer
  // aligns with the downsampled line — drop it (the traffic-colored line is
  // meaningless at cross-country zoom). Navigation restores the full line via
  // navCoordinates; it just won't be congestion-colored on these extreme trips.
  r.congestion = undefined;
}
export function activeTravelTooFar(
  mode: TravelMode,
  tripMiles: number,
): string | null {
  const max = ACTIVE_TRAVEL_MAX_MI[mode];
  if (max == null || !(tripMiles > max)) return null;
  const verb = mode === "walking" ? "walk" : "bike";
  // Aug 19 units audit: the threshold above stays in miles (it's a fitness
  // judgement, not display), but the user-facing distance goes through
  // fmtDistance so a metric driver isn't told "about 40 mi".
  return `That's about ${fmtDistance(tripMiles * 1609.344)} — too far to ${verb}. Switch to Drive for a trip this long.`;
}

/**
 * Fan out routing requests and score the results. This was previously the
 * inline `generate` closure inside Index.tsx — extracted so the host
 * component is no longer 2.2k lines and so the pipeline can be unit-tested
 * in isolation.
 *
 * Side effects flow back through `ctx` setters: the caller still owns the
 * UI state. Generation is cooperatively cancellable via `generationIdRef`.
 */
/**
 * CARDS MAY NEVER GO BACKWARDS (Morgan: "it came back with 2 routes and then
 * compressed them into one" — and, earlier, "wait what just happened").
 *
 * The ratchet further down enforces this, but only inside
 * `if (authoritativeCardsEmitted && authoritativeClassified)` — i.e. only once
 * an ENRICHED classification has rendered (pass 1, or the pass-2 final). The
 * streaming passes before it (`setScored(refreshed)`, `setScored(upgraded)`)
 * replace the visible set wholesale, so a pass returning two cards could be
 * overwritten by a pass returning one. On a long trip that streaming window is
 * many seconds wide, and the user watches options vanish.
 *
 * (Aug 23) That window is now much narrower: `authoritativeCardsEmitted` is set
 * by PASS 1, before discovery, rather than by the final classify at the end —
 * so the cheap streaming passes are gated off far earlier in the run.
 *
 * This applies the same guarantee to those earlier passes: the incoming set
 * wins for every slot it fills, and any classification the user could already
 * see that the new set does NOT contain is carried forward rather than
 * dropped. A later pass may add or improve; it may never take away.
 */
/** Sources that exist because Pleasant DECIDED this drive was worth building —
 *  curated roads, the coastal thread, discovery corridors. The opposite of an
 *  anonymous engine alternative that happens to score well. */
const DELIBERATE_SCENIC_SOURCES = new Set([
  "coastal-pch",
  "scenic-road",
  "scenic-corridor",
  "calm-corridor",
]);

function isDeliberateScenic(c: ClassifiedRoute): boolean {
  return DELIBERATE_SCENIC_SOURCES.has(getRouteSource(c.route) ?? "");
}

/** A road a PERSON chose, as opposed to a corridor we assembled.
 *  scenic-corridor / calm-corridor are machine-picked waypoints — the same
 *  class the ring filter exists to police. */
const HUMAN_CURATED_SOURCES = new Set(["scenic-road", "coastal-pch"]);
function isHumanCuratedScenic(c: ClassifiedRoute): boolean {
  return HUMAN_CURATED_SOURCES.has(getRouteSource(c.route) ?? "");
}

/** RING FILTER (Morgan, Aug 3: "get rid of all this doubling back on the
 *  same road" — the SF→Portland balloons around Fortuna/Eureka). Those loops
 *  are RINGS: out one road, back a different one, so they pass
 *  trimOutAndBackSpurs (same-road spurs only) AND contiguousRetraceMeters
 *  (same-pavement only). The distinguishing sin is spending many miles to end
 *  up right next to where you already were. A shoreline town-touch returns to
 *  the corridor within a few km of driving; the Eureka balloons burn 20–50 km
 *  to come back beside their own line. So: flag any point that comes within
 *  2 km of a spot ≥12 km EARLIER along the drive. The 427-minute
 *  charming-towns grand tour and the Camden spur (~8 km round trip) stay
 *  under the 12 km gap; only genuine rings trip it. Applied to CONSTRUCTED
 *  candidates only — we built their waypoints, so their rings are our fault.
 *  Curated scenic-road stays exempt (a curator chose that road on purpose),
 *  and Pleasant Loops trips are rings by design (exempted at call sites). */
const RING_MIN_PATH_GAP_M = 12_000;
const RING_NEAR_M = 2_000;
function ringDetected(coords: LngLat[]): boolean {
  const pts = samplePolylineMax(coords, 600);
  const cum: number[] = [0];
  for (let i = 1; i < pts.length; i++) cum[i] = cum[i - 1] + haversine(pts[i - 1], pts[i]);
  for (let j = 2; j < pts.length; j++) {
    for (let i = 0; i < j; i++) {
      if (cum[j] - cum[i] < RING_MIN_PATH_GAP_M) break; // later i are only closer along the path
      if (haversine(pts[i], pts[j]) < RING_NEAR_M) return true;
    }
  }
  return false;
}
// coastal-pch is NOT here (Morgan, Aug 3, same night the filter shipped):
// the Lake Michigan shoreline circles drowned river-mouth lakes — Muskegon
// Lake, White Lake, Pentwater — whose banks sit <2km apart across a 15–20km
// drive around. Geometrically a ring; experientially the whole point. The
// coastal thread's vias are Morgan-curated shoreline samples — the same
// trust class as scenic-road, so it gets the same exemption. The filter
// exists for MACHINE-picked waypoints (discovery corridors), whose rings
// are our fault. coastal-pch keeps its 2500m same-pavement retrace leash.
const CONSTRUCTED_SOURCES = new Set([
  "scenic-corridor", "calm-corridor", "valhalla-adventure", "scenic-anchor",
]);
function isConstructedRoute(r: RawRoute): boolean {
  return CONSTRUCTED_SOURCES.has(getRouteSource(r));
}

function neverShrink(
  prev: ClassifiedRoute[],
  next: ClassifiedRoute[],
): ClassifiedRoute[] {
  if (prev.length === 0) return next;
  // THE RATCHET, part 2 — QUALITY (Morgan, Aug 3: "the problem is it works
  // and then the route is disappeared again" / "get rid of whatever is
  // eliminating good routes"). Log-proven failure: Holland→Traverse City
  // showed scenic=coastal-pch (the real lakeshore) at 1.5s; every [coastal]
  // SLOT pass agreed it was eligible and top; the final classify seated
  // scenic=valhalla-adventure anyway and the shoreline vanished. Same
  // pathology killed Maine's Route 1 card. Rule: once the scenic slot shows
  // a DELIBERATE route, a later pass may replace it only with another
  // deliberate route. Anonymous engine candidates can fill an empty scenic
  // slot; they can never evict a curated/constructed one.
  const prevScenic = prev.find((c) => c.classification === "scenic");
  let guarded = next;
  // EVERY scenic swap announces itself, decision or not (rule 17). The Aug 8
  // Flagstaff run showed Route 66 replaced by a corridor with NO ratchet line
  // at all, which left two indistinguishable explanations: the guard declined,
  // or the swap never passed through here. This line separates them.
  {
    const nextScenicDbg = next.find((c) => c.classification === "scenic");
    const before = prevScenic ? getRouteSource(prevScenic.route) ?? "engine" : "none";
    const after = nextScenicDbg ? getRouteSource(nextScenicDbg.route) ?? "engine" : "none";
    if (before !== after) console.log(`[ratchet] scenic swap seen: ${before} → ${after}`);
  }
  // THE RATCHET, part 2b — CURATED IS ITS OWN RUNG (Morgan, Aug 8, San Jose →
  // Flagstaff on build 254bea5). The log is unambiguous:
  //   4.2s  [cards] scenic/src=scenic-road/1296m      ← Route 66 wins
  //   46.5s [perf] late fold: +1 discovery routes joined after deadline
  //   46.6s [cards] scenic/src=scenic-corridor/1332m  ← and loses it again
  // The quality guard below passed the swap because scenic-corridor IS
  // deliberate — but curated → constructed is a DOWNGRADE, and the ratchet
  // was blind to it. Rule 13 says a shown deliberate route never disappears;
  // rule 16 says provenance decides. Together: once the Scenic slot shows a
  // road a HUMAN curated, only another human-curated road may take its place.
  if (prevScenic && isHumanCuratedScenic(prevScenic)) {
    const nextScenic = next.find((c) => c.classification === "scenic");
    if (!nextScenic || !isHumanCuratedScenic(nextScenic)) {
      const kept = [
        ...next.filter((c) => c.classification !== "scenic" && c.route !== prevScenic.route),
        prevScenic,
      ];
      console.log(
        `[ratchet] kept CURATED scenic=${getRouteSource(prevScenic.route)} — refused ${
          nextScenic ? `downgrade to ${getRouteSource(nextScenic.route) ?? "unknown"}` : "deletion"
        }`,
      );
      // Fall through to the count ratchet with the curated route restored.
      const haveKept = new Set(kept.map((c) => c.classification));
      const carriedK = prev.filter(
        (c) => !haveKept.has(c.classification) && !kept.some((n) => n.route === c.route),
      );
      return carriedK.length === 0 ? kept : [...kept, ...carriedK];
    }
  }
  if (prevScenic && isDeliberateScenic(prevScenic)) {
    const nextScenic = next.find((c) => c.classification === "scenic");
    if (!nextScenic || !isDeliberateScenic(nextScenic)) {
      guarded = [
        ...next.filter((c) => c.classification !== "scenic" && c.route !== prevScenic.route),
        prevScenic,
      ];
      console.log(
        `[ratchet] kept scenic=${getRouteSource(prevScenic.route)} — refused ${
          nextScenic ? `downgrade to ${getRouteSource(nextScenic.route) ?? "unknown"}` : "deletion"
        }`,
      );
    }
  }
  // Part 1 — COUNT: any classification the user could already see that the
  // new set does not contain is carried forward rather than dropped.
  const haveNext = new Set(guarded.map((c) => c.classification));
  const carried = prev.filter(
    (c) => !haveNext.has(c.classification) && !guarded.some((n) => n.route === c.route),
  );
  if (carried.length === 0) return guarded;
  return [...guarded, ...carried];
}

/** One route's enrichment result, kept alongside the inputs that produced it.
 *
 *  `inputs` exists for ONE reason (Morgan, Aug 23, decision 3): pass 2 can
 *  discover a route faster than anything pass 1 saw, and `fastestDuration` is
 *  an input to scoring, not a display value. When the pool minimum moves, every
 *  already-enriched route must be re-scored against the new minimum — and
 *  re-scoring must not mean re-fetching. Everything expensive is in here. */
export interface EnrichedRoute {
  route: RawRoute;
  scored: ScoredRoute;
  /** Every scoreRoute argument EXCEPT fastestDuration. */
  inputs: Omit<Parameters<typeof scoreRoute>[0], "fastestDuration">;
  /** True when this route went through the full OSM / scenery / POI path.
   *  False means it carries the cheap baseline — the thing rule 20 is about. */
  deep: boolean;
}

export interface EnrichPoolContext {
  fastestDuration: number;
  /** Routes to enrich fully. Everything else gets the cheap baseline. */
  deepEnrich: Set<RawRoute>;
  /** Subset of deepEnrich allowed to make the PAID places call. */
  placesEnrich: Set<RawRoute>;
  routePrefs: Preference[];
  profile: PleasantnessProfile;
  prioritizeCommunity?: boolean;
  toleranceMultiplier: number;
  mode: TravelMode;
  buffer: number;
  sampleCap: number;
  enclosingParksPromise: Promise<string[]>;
  /** Wall-clock the DEEP pass gets. Cheap scoring is never raced. */
  budgetMs: number;
  /** "pass 1" / "pass 2" — appears in every log line this pass emits. */
  label: string;
  /** True once this search has been superseded by a newer one (PLE-6).
   *  The deep pass checks it before its heavy synchronous scoring. */
  isCancelled?: () => boolean;
}

export interface EnrichPoolResult {
  /** One entry per input route, always. */
  routes: EnrichedRoute[];
  /** False when the deep pass did not finish inside its budget. The caller
   *  MUST NOT treat `routes` as authoritative when this is false: the deep
   *  targets fell back to the cheap baseline, which is precisely the blind
   *  data the ceiling exists to refuse. */
  ok: boolean;
  deepAttempted: number;
  /** How many deep routes had settled when the budget ran out. Nonzero with
   *  ok=false is the interesting case — it names the cost of the ceiling. */
  deepSettled: number;
  ms: number;
}

const EMPTY_OSM = () => ({
  parks: 0, waterBodies: 0, residentialKm: 0, highwayKm: 0, majorRoadKm: 0,
  bikeLaneKm: 0, sidewalkKm: 0, footwayKm: 0, noSidewalkKm: 0,
});

/**
 * FULL ENRICHMENT + SCORING FOR A POOL OF ROUTES.
 *
 * Extracted verbatim from the single inline `raw.map(...)` block (Morgan,
 * Aug 23) so it can be CALLED TWICE: once on the core router pool before
 * discovery blocks for up to 12 s, and once on whatever discovery adds. The
 * scoring itself is untouched — same calls, same order, same arguments.
 *
 * Two structural differences from the inline version, both required by the
 * reorder:
 *
 *  1. Cheap scoring is computed synchronously and is NEVER raced. Previously a
 *     single Promise.all covered cheap and deep alike, so a timeout discarded
 *     the cheap scores too. Now the budget applies only to the work that can
 *     actually blow it.
 *  2. The result reports `ok` instead of the caller comparing against a
 *     "timeout" sentinel, and carries `inputs` for decision-3 re-scoring.
 */
export async function enrichPool(
  routes: RawRoute[],
  ctx: EnrichPoolContext,
): Promise<EnrichPoolResult> {
  const t0 = performance.now();
  const cheap = (r: RawRoute): EnrichedRoute => {
    const features = summarizeFeatures(
      r, EMPTY_OSM(), { cafes: [], shops: [], landmarks: [] }, ctx.buffer,
    );
    const inputs = {
      route: r, features,
      prefs: ctx.routePrefs, community: 50,
      profile: ctx.profile,
      toleranceMultiplier: ctx.toleranceMultiplier,
      mode: ctx.mode,
    };
    return {
      route: r,
      scored: scoreRoute({ ...inputs, fastestDuration: ctx.fastestDuration }),
      inputs, deep: false,
    };
  };

  const deepTargets = routes.filter((r) => ctx.deepEnrich.has(r));
  const shallow = routes.filter((r) => !ctx.deepEnrich.has(r)).map(cheap);
  if (deepTargets.length === 0) {
    return {
      routes: shallow, ok: true, deepAttempted: 0, deepSettled: 0,
      ms: Math.round(performance.now() - t0),
    };
  }

  let deepSettled = 0;
  // A superseded search's result is never read (its caller returns on
  // isCancelled right after enrichPool), so the cheap baseline is returned
  // only to keep the pool's shape.
  const skipSuperseded = (r: RawRoute, where: string): EnrichedRoute => {
    // eslint-disable-next-line no-console
    console.log(`[enrich] ${ctx.label} SKIPPED deep scoring ${where} — search superseded (${r.coordinates.length} pts)`);
    return cheap(r);
  };
  const deepPromise = Promise.all(deepTargets.map(async (r): Promise<EnrichedRoute> => {
    const samples = samplePolylineMax(r.coordinates, ctx.sampleCap);
    const routeBox = bbox(r.coordinates, 0.005);
    // PER-DEPENDENCY TIMING (Morgan, Aug 22). The deadline fix removed the
    // 23s edge-budget contradiction, and Overpass now answers in ~4.5s -
    // but enrichment STILL misses the ceiling, and only four of the ~nine
    // calls three routes should issue ever logged a completion. The rest
    // were pending when the ceiling hit. Which ones, we could not say:
    // every call logged as "unlabelled". This names each dependency and
    // reports its settle time, so the next run says where the time goes
    // instead of leaving it to inference. Diagnostic only - `stage` is
    // pass-through and changes no value and no ordering.
    const __eIdx = String((r as { id?: string }).id ?? "?").slice(0, 6);
    const stage = <T,>(name: string, pr: Promise<T>): Promise<T> => {
      const t = performance.now();
      return pr.then(
        (v) => {
          // eslint-disable-next-line no-console
          console.log(`[enrich] ${ctx.label} r${__eIdx} ${name} ok in ${Math.round(performance.now() - t)}ms`);
          return v;
        },
        (e) => {
          // eslint-disable-next-line no-console
          console.log(`[enrich] ${ctx.label} r${__eIdx} ${name} FAILED in ${Math.round(performance.now() - t)}ms — ${String(e)}`);
          throw e;
        },
      );
    };
    const [osmAndPts, osmEnv, scenicScore, cafes, shops, landmarks, googlePlaces] = await Promise.all([
      stage("osmFeatures", fetchOsmFeaturesAndPoints(r.coordinates)),
      stage("osmEnv", fetchOsmEnvironment(r.coordinates)),
      // OUR OWN SCENERY LAYER, alongside Overpass rather than instead of
      // it. Overpass still supplies the road-surface fields (sidewalks,
      // bike lanes, lighting) that the layer deliberately does not hold;
      // the layer supplies WHAT KIND OF GREEN, which Overpass cannot
      // answer outside the US Northeast because that is all it holds.
      //
      // In this Promise.all on purpose: it runs concurrently with calls
      // already being made, so it costs wall-clock only if it is slower
      // than the slowest of them. Never throws — a failure returns an
      // UNMEASURED verdict, which the copy treats as "say nothing", not
      // as "no scenery here" (rule 20).
      stage("scenicLayer", import("@/api/scenicScoring")
        .then((m) => m.fetchScenicScoring(r.coordinates)))
        // Typed as the real return shape, not a bare literal. Without the
        // annotation the two arms of the union have no `region` in common,
        // and the read below is a type error - which is the compiler
        // correctly pointing out that the failure path was missing a field
        // the success path carries.
        .catch((): ScenicScoringResult => ({
          summary: null,
          coveredFraction: 0,
          region: undefined,
          named: [],
          ms: 0,
        })),
      stage("poi:cafe", searchPoisInBbox(routeBox, "cafe", 25)),
      stage("poi:restaurant", searchPoisInBbox(routeBox, "restaurant", 25)),
      stage("poi:landmark", searchPoisInBbox(routeBox, "landmark", 25)),
      // Places along the route — FREE Overpass first (self-hosted
      // instance via VITE_OVERPASS_ENDPOINT, mirrors as fallback), paid
      // Google SearchNearby ONLY when Overpass returns nothing (outage /
      // uncovered region). POST_LAUNCH_TODO item 0: SearchNearby was the
      // dominant API cost (~$30/1000 calls); at 10k users the old path
      // projected ~$2.5-3k/mo, the Overpass path ~$0. Same COST GUARD
      // cap applies to the fallback. Same GooglePlaceHit shape, so
      // scoring is unchanged.
      // THE FALLBACK RUNS ONCE, AND ITS FAILURE IS EMPTY, NOT FATAL
      // (Aug 19 audit, F2). The old chain put .catch AFTER .then, so a
      // failing Google fallback was caught and fired AGAIN - two paid
      // calls back to back against a 10/min limit - and if the second
      // also threw, the rejection escaped, collapsed the enrichment
      // Promise.all, and the WHOLE generation showed "We've hit our
      // routing limit" over a bonus POI signal. Places are additive:
      // no places means fewer cafe chips, never no route.
      ctx.placesEnrich.has(r)
        ? stage("places", searchPlacesAlongRouteOverpass(r.coordinates, { radiusM: 250 })
            .catch(() => [] as Awaited<ReturnType<typeof searchPlacesAlongRouteOverpass>>)
            .then((hits) =>
              hits.length
                ? hits
                : searchPlacesAlongRoute(r.coordinates, {
                    sampleSpacingM: 400,
                    radiusM: 250,
                    maxSamples: 8,
                  }).catch((e) => {
                    console.log(`[places] fallback failed (kept going): ${String((e as Error)?.message ?? e)}`);
                    return [];
                  }),
            ))
        : Promise.resolve([]),
    ]);
    // eslint-disable-next-line no-console
    console.log(`[enrich] ${ctx.label} r${__eIdx} ALL DEPENDENCIES SETTLED`);
    const osm = osmAndPts.features;
    const osmPoints = osmAndPts.points;
    // Carry the route's named scenic features (with coordinates) so
    // scenic narration can fire on the route's own rivers and parks,
    // not only on Google POIs.
    r.scenicPoints = buildScenicPoints(r, osmPoints);

    // SUPERSEDED SEARCHES STOP HERE (PLE-6, Oct 5). Every state write from a
    // superseded search is already dropped by the guarded setters, so the
    // scoring below can only burn time -- and it is synchronous. Profiled in
    // the Simulator: a Maine search the user had already replaced with two
    // Hackley searches froze the app for 471.7 s in segmentRoute when its
    // 129,954-element Overpass result finally landed. The network fetches
    // above still ran to completion and warmed their caches; buildScenicPoints
    // above still ran because it writes onto the route object. This skips
    // only the scoring, and confesses when it does (rule 17).
    if (ctx.isCancelled?.()) return skipSuperseded(r, "before summarizeFeatures");

    const features = summarizeFeatures(r, osm, { cafes, shops, landmarks }, ctx.buffer, { osmPoints, osmEnv });
    // Carried, not merged. `features.parks` keeps meaning exactly what it
    // meant yesterday so every existing scorer path is untouched; readers
    // that want the breakdown opt in via features.scenicLayer.
    features.scenicLayer = {
      summary: scenicScore.summary,
      coveredFraction: scenicScore.coveredFraction,
      region: scenicScore.region,
      named: scenicScore.named,
    };
    // Verified through-parks: the corridor `is_in` containment result (the
    // route VERIFIABLY runs inside these). Awaiting the SHARED promise here
    // adds no extra call and runs concurrently with this route's other
    // enrichment. Kept SEPARATE from parkNames so a park the route merely
    // passes near is never claimed as "through {park}" (Morgan).
    const corridorEnclosingParks = await ctx.enclosingParksPromise;
    if (corridorEnclosingParks.length) features.throughParkNames = corridorEnclosingParks;

    // Walk Score lookup (#99). Walking-mode-only external-data signal,
    // fetched via the walk-score edge function (Walk Score's API must be
    // called server-side). No-op when WALK_SCORE_KEY isn't configured.
    // Cached 30 days at ~250m grid so most walks add ≤ 1 net API call on
    // the steady state. Failure is silent — features.walkScore stays
    // undefined and the scorer skips the boost.
    if (ctx.mode === "walking") {
      try {
        const { walkScoreAlongRoute } = await import("@/api/walkScore");
        const ws = await walkScoreAlongRoute(r.coordinates);
        if (typeof ws === "number") features.walkScore = ws;
      } catch { /* ignore — Walk Score is an additive signal */ }
    }
    // Walk Score's Bike Score (#99b). Biking-mode-only, same server proxy
    // / same vendor as Walk Score — bike=1 returns it for free. Sampled
    // along the route, additive only.
    if (ctx.mode === "biking") {
      try {
        const { bikeScoreAlongRoute } = await import("@/api/walkScore");
        const bs = await bikeScoreAlongRoute(r.coordinates);
        if (typeof bs === "number") features.bikeScore = bs;
      } catch { /* ignore — additive signal */ }
    }
    // People for Bikes city-level lookup (#105). Biking-mode-only,
    // static-data, no network. Returns null when trip doesn't sit
    // near a rated city — small lever, additive only.
    if (ctx.mode === "biking") {
      try {
        const { peopleForBikesScoreForRoute } = await import("@/api/peopleForBikes");
        const pfb = peopleForBikesScoreForRoute(r.coordinates);
        if (typeof pfb === "number") features.peopleForBikesScore = pfb;
      } catch { /* ignore — additive signal */ }
    }
    // Median home value lookup (#100 / #107). All modes. Tries the
    // embedded v1 snapshot first; if no match, falls back to live
    // Census ACS Geocoder + Data API (US-only). Census results
    // cached for 30 days at ~250m grid, so the cost amortizes to
    // ~0 API calls on the steady state. Small lever, additive only.
    try {
      const { homeValueForRouteWithFallback } = await import("@/api/medianHomeValue");
      const medianUsd = await homeValueForRouteWithFallback(r.coordinates);
      if (typeof medianUsd === "number") features.medianHomeValueUsd = medianUsd;
    } catch { /* ignore — additive signal */ }
    // Elevation profile (#109). Biking + walking only — cars don't
    // care about hills for pleasantness. Open-Meteo Elevation API
    // is free, no key. Failure is silent.
    if (ctx.mode === "biking" || ctx.mode === "walking") {
      try {
        const { elevationProfileForRoute } = await import("@/api/elevation");
        const elev = await elevationProfileForRoute(r.coordinates);
        if (elev) {
          features.totalAscentM = elev.totalAscentM;
          features.maxGradePct = elev.maxGradePct;
        }
      } catch { /* ignore — additive signal */ }
    }
    // Weather (#110) — all modes. Open-Meteo Weather API, free.
    try {
      const { fetchWeatherForRoute } = await import("@/api/weather");
      const w = await fetchWeatherForRoute(r.coordinates);
      if (w) {
        features.precipitationMmHr = w.precipitationMmHr;
        features.temperatureC = w.temperatureC;
        features.windKmh = w.windKmh;
      }
    } catch { /* ignore — additive signal */ }
    // Air quality (#111) — biking + walking only. Driving is
    // mostly insulated. Open-Meteo Air Quality API, free.
    if (ctx.mode === "biking" || ctx.mode === "walking") {
      try {
        const { fetchAqiForRoute } = await import("@/api/airQuality");
        const aqi = await fetchAqiForRoute(r.coordinates);
        if (typeof aqi === "number") features.usAqi = aqi;
      } catch { /* ignore — additive signal */ }
    }
    // Sun position (#113 / #114) — all modes. Pure math, no API,
    // no network. Walking uses high sun for the shade bonus
    // (#113); all modes use low sun for the night-lighting rule
    // (#114).
    if (r.coordinates.length > 0) {
      try {
        const { sunPositionAt } = await import("@/utils/sunPosition");
        const [lng, lat] = r.coordinates[0];
        const sun = sunPositionAt(lat, lng);
        features.sunAltitudeDeg = sun.altitudeDeg;
      } catch { /* ignore — additive signal */ }
    }

    let community = 50;
    let communityTags: CommunityTag[] = [];
    try {
      const t = await fetchTagsInBbox({
        minLat: routeBox.minLat, maxLat: routeBox.maxLat,
        minLng: routeBox.minLng, maxLng: routeBox.maxLng,
      });
      communityTags = t;
      community = communityScore(r, t, ctx.buffer);
    } catch { /* ignore — keep baseline */ }

    void samples;

    // Checked again: the awaits since the first check give a newer search
    // time to start, and scoreRoute -> segmentRoute is the expensive part.
    if (ctx.isCancelled?.()) return skipSuperseded(r, "before scoreRoute");

    const inputs = {
      route: r, features,
      prefs: ctx.routePrefs, community,
      prioritizeCommunity: ctx.prioritizeCommunity,
      profile: ctx.profile,
      osm, osmPoints, osmEnv,
      pois: { cafes, shops, landmarks },
      googlePlaces,
      communityTags,
      buffer: ctx.buffer,
      toleranceMultiplier: ctx.toleranceMultiplier,
      mode: ctx.mode,
    };
    deepSettled++;
    return {
      route: r,
      scored: scoreRoute({ ...inputs, fastestDuration: ctx.fastestDuration }),
      inputs, deep: true,
    };
  }));

  const raced = await Promise.race([
    deepPromise,
    new Promise<"timeout">((res) =>
      setTimeout(() => res("timeout"), Math.max(0, ctx.budgetMs))),
  ]);
  const ms = Math.round(performance.now() - t0);
  if (raced === "timeout") {
    // The deep work keeps running underneath (it warms the caches for a
    // re-search); its result is discarded for THIS search, exactly as before.
    // The deep targets fall back to the cheap baseline so the pool is complete
    // — but ok=false, and no caller may classify off this and call it final.
    return {
      routes: [...shallow, ...deepTargets.map(cheap)],
      ok: false, deepAttempted: deepTargets.length, deepSettled, ms,
    };
  }
  return {
    routes: [...shallow, ...raced],
    ok: true, deepAttempted: deepTargets.length, deepSettled, ms,
  };
}

/**
 * DECISION 3 (Morgan, Aug 23): "If pass 2 discovers a genuinely faster route,
 * re-score. Do not reject it as 'implausible'. fastestDur affects scoring, so
 * if the minimum changes, confess it in diagnostics and recompute the affected
 * scoring against the new fastest duration."
 *
 * Re-runs scoreRoute for every entry against the new pool minimum, reusing the
 * inputs already fetched. No network, no re-enrichment.
 */
export function rescoreForFastest(
  entries: EnrichedRoute[],
  fastestDuration: number,
): EnrichedRoute[] {
  return entries.map((e) => ({
    ...e,
    scored: scoreRoute({ ...e.inputs, fastestDuration }),
  }));
}

export async function runRouteGeneration(
  args: GenerateRoutesArgs,
  ctx: RouteGenerationContext,
): Promise<void> {
  const {
    startCoords, dest, waypoints = [], detourMeta, modeOverride, tryAgainOpts,
    googleCoords,
  } = args;
  const {
    mode, profile, prioritizeCommunity,
    generationIdRef, triedWaypointsRef, shownPleasantRef,
  } = ctx;

  const effectiveMode: TravelMode = modeOverride ?? mode;
  const effectiveProfile: PleasantnessProfile = tryAgainOpts && tryAgainOpts.attempt > 0
    ? PROFILE_ROTATION[tryAgainOpts.attempt % PROFILE_ROTATION.length]
    : profile.pleasantnessProfile;
  // Real-drive note (#83): the previous gate required tolerance === "scenic"
  // AND a water/nature profile. So a user on the default Pleasant tolerance
  // never got the hardcoded Tarrytown reservoir route in their candidate
  // pool, and the recommended route ran through downtown by default.
  // Real-drive note (#89): we briefly gated this to Pleasant-only to try to
  // differentiate Scenic, but that pushed Scenic back through downtown.
  // Restored to both Pleasant AND Scenic — they differ because the
  // scenic-tier wider-net probing below adds 4× wider anchors on TOP of
  // the reservoir base for Scenic, threading the trip further out.
  // (#176) Disabled the hardcoded localBackroadSequence override. The
  // overlay diagnostic on Hackley School revealed it was matching on
  // Sleepy Hollow → Tarrytown trips, adding waypoints, and forcing
  // primaryWaypoints to be non-empty — which SKIPPED the entire
  // fetchThreePoolsAndDedup + Valhalla pipeline and fell back to a
  // single Mapbox alternatives call. Result: pool of 3 near-identical
  // routes, no Valhalla, Pleasant collapses to Direct. Macpherson worked
  // because localBackroadSequence didn't match there. Disabling the
  // override lets Valhalla's graph-level highway avoidance discover the
  // reservoir + scenic corridors naturally on every driving trip with
  // no user waypoints. Per Morgan: routing engines don't return loopy
  // routes on their own — we made them loopy by feeding them waypoints.
  const shouldForceLocalBackroad = false;
  const localSeq: ReturnType<typeof localBackroadSequence> = null;
  const primaryWaypoints = localSeq
    ? localSeq.waypoints.map((w) => w.coord)
    : waypoints;
  const myId = ++generationIdRef.current;
  const isCancelled = () => generationIdRef.current !== myId;

  // GUARDED setters: every state write goes through this proxy, which drops
  // the call when the generation has been superseded. Even if a downstream
  // `if (isCancelled()) return` is missed, stale results can never overwrite
  // newer ones — the write itself becomes a no-op. This is the source of
  // truth for cancellation semantics.
  const g = {
    setLoading: (v: boolean) => { if (!isCancelled()) ctx.setLoading(v); },
    setAppliedDetour: (v: AppliedDetour) => { if (!isCancelled()) ctx.setAppliedDetour(v); },
    setFastestPleasantBaseline: (v: number | undefined) => { if (!isCancelled()) ctx.setFastestPleasantBaseline(v); },
    // EVERY WRITER CONFESSES (rule 17, rule 19). `scored` is what the display
    // reads, and it is written from six different places in this file. On
    // current location → Nice the engine's own `[pleasant-door]` line said it
    // chose valhalla-balanced/378m, while the display was holding
    // mapbox-pleasant/913m — a route classifyThreeCards never picked. So some
    // OTHER writer put it there, and with six candidate sites and no log there
    // was no way to tell which. This is the single choke point they all pass
    // through: name the caller and the Pleasant card it is installing.
    setScored: (v: ClassifiedRoute[] | ((prev: ClassifiedRoute[]) => ClassifiedRoute[])) => {
      if (isCancelled()) return;
      // Caller identity from the stack: frame 0 is Error, 1 is this arrow,
      // 2 is whoever called g.setScored. Diagnostic only.
      const site = (new Error().stack ?? "").split("\n")[2]?.trim().slice(0, 90) ?? "unknown";
      // Identity, not just source+duration: Mapbox returns TWO avoid-highways
      // routes on this trip (913m and 960m) and the backtrack filter drops one
      // of them, so "mapbox-pleasant" alone can't tell us WHICH object survived.
      const describe = (list: ClassifiedRoute[]) => {
        const p = list.find((c) => c.classification === "pleasant");
        return p
          ? `${getRouteSource(p.route) ?? "engine"}/${Math.round(p.route.duration / 60)}m` +
              `/#${Math.round(p.route.distance / 100)}/${p.route.coordinates.length}pts` +
              `${p.sameAsClass ? `/sameAs=${p.sameAsClass}` : ""}`
          : "none";
      };
      if (typeof v === "function") {
        ctx.setScored((prev) => {
          const next = (v as (p: ClassifiedRoute[]) => ClassifiedRoute[])(prev);
          if (describe(prev) !== describe(next)) {
            // eslint-disable-next-line no-console
            console.log(
              `[scored-write] pleasant ${describe(prev)} → ${describe(next)} ` +
                `(${prev.length}→${next.length} cards) @ ${site}`,
            );
          }
          return next;
        });
        return;
      }
      // eslint-disable-next-line no-console
      console.log(`[scored-write] pleasant := ${describe(v)} (${v.length} cards) @ ${site}`);
      ctx.setScored(v);
    },
    setSelectedClass: (v: "fastest" | "scenic" | "pleasant") => { if (!isCancelled()) ctx.setSelectedClass(v); },
    setTags: (v: CommunityTag[]) => { if (!isCancelled()) ctx.setTags(v); },
    setRouteDiagnostics: ctx.setRouteDiagnostics
      ? (v: RouteDiagnostics | null) => { if (!isCancelled()) ctx.setRouteDiagnostics?.(v); }
      : undefined,
    setStatus: (s: RouteGenStatus, error?: string | null) => { if (!isCancelled()) ctx.setStatus(s, error ?? null); },
  };

  g.setStatus("loading", null);
  g.setLoading(true);

  // [perf] Stage timers — temporary instrumentation to find why the Pleasant
  // route can take minutes. Each stage logs ms since the user pressed Go.
  const T0 = performance.now();
  // READY DEADLINE, ARMED FOR THE WHOLE RUN (Morgan, Aug 22). Every Overpass
  // path in this generation now knows the wall-clock moment after which its
  // answer is worthless, and refuses to start work that cannot finish by then.
  // Armed here rather than at the enrichment block on purpose: the calls made
  // BEFORE enrichment are the ones that push its start out to 3-13s, and they
  // are bound by the same ceiling. Disarmed in the finally below so a later
  // caller with genuine time is not silently constrained by a stale deadline.
  setOverpassDeadline(T0 + READY_CEILING_MS);
  // SEARCH TELEMETRY (Morgan, Aug 24, follow-up #1). Observation only. This
  // arms no deadline, changes no budget, and adds no await to any path — the
  // question it exists to answer is "how far past the ceiling do degraded
  // searches actually land", which nothing currently persists. Exactly one row
  // is written, in the finally below; the verdict and the numbers are stamped
  // at whichever terminal point this particular search reaches.
  const _telemetry = beginSearch();
  let _telemetryVerdict: EnrichmentVerdict = "unmeasured";
  let _telemetryBudgetMs = READY_CEILING_MS;
  let _telemetryRouteM: number | null = null;
  const perf = (label: string) =>
    console.log(`[perf] ${label}: ${Math.round(performance.now() - T0)}ms`);
  g.setAppliedDetour(detourMeta ?? (localSeq ? {
    summary: `Routed via ${localSeq.label}.`,
    waypoints: [{ label: localSeq.label, reason: localSeq.reason }],
  } : undefined));
  // ---- THE SEQUENCE IS LITERAL (Morgan, Aug 24) ----------------------------
  // "core routes → pass-1 enrichment → authoritative cards → THEN launch
  //  discovery. Do not let discovery start in the background before pass 1 if
  //  it competes for the same Overpass capacity."
  //
  // The reorder already put enrichment FIRST in time. It did not stop discovery
  // running underneath, and measurement showed why that matters: the private
  // Overpass box executes at most THREE queries at once (measured — `Rate
  // limit: 0`, per-query space irrelevant, offered 8 and never saw more than 3
  // running), and one search was asking it for enrichment cells and discovery
  // corridors simultaneously. Two consumers, three slots, and the cards lose.
  //
  // So discovery now genuinely waits. Every discovery promise below opens with
  // `await discoveryGate`, and the gate is released once pass 1 is done — or in
  // the `finally`, so the ceiling path can never leave it closed and strand the
  // late fold that waits on those same promises.
  let releaseDiscovery: () => void = () => {};
  const discoveryGate = new Promise<void>((res) => { releaseDiscovery = res; });

  try {
    // Tolerance-aware highway-avoidance budget; SHORT-TRIP override forces
    // Infinity for driving < ~10 mi straight-line so backroads always win.
    const tripMilesStraight = haversine(startCoords, dest.coords) / 1609.34;

    // Guard absurd active-travel distances up front: Mapbox returns nothing for
    // a 190-mi "walk", which otherwise becomes an empty pool → "No routes
    // found" → a blank screen. Hand back mode-specific guidance instead, and
    // clear any stale cards from the previous mode so the message stands alone.
    const tooFarMsg = activeTravelTooFar(effectiveMode, tripMilesStraight);
    if (tooFarMsg) {
      g.setScored([]);
      g.setStatus("error", tooFarMsg);
      return;
    }

    // SPEED: kick the base Mapbox pool off RIGHT NOW for a driving point-to-point
    // trip — it only needs the two endpoints, so it has no reason to wait behind
    // the per-state great-drives JSON load below. Firing it here overlaps the
    // Mapbox round-trip with that chunk load (and with scenic-match + injection
    // setup), shaving that time off first paint. `await`ed later at the batch.
    const earlyBasePool: Promise<RawRoute[]> | null =
      effectiveMode === "driving" && primaryWaypoints.length === 0
        ? fetchThreePoolsAndDedup(startCoords, dest.coords)
        : null;

    // Driving roads are code-split PER STATE. Pull only the states this trip
    // touches (the straight line from start to destination) before the scenic
    // matcher and scoring run, so a drive can route toward great roads and away
    // from avoid roads. A NY→CT trip downloads ~2 small chunks, not the whole
    // ~2.6MB national store. Idempotent + cached. Walks/bikes load nothing.
    if (effectiveMode === "driving") {
      const [{ loadGreatDrivesForStates }, { stateAbbrsAlongRoute }] = await Promise.all([
        import("@/utils/scenicRoads"),
        import("@/utils/regionsAlongRoute"),
      ]);
      const tripStates = stateAbbrsAlongRoute([startCoords, dest.coords]);
      await loadGreatDrivesForStates(tripStates);
    }

    const shortDrive = effectiveMode === "driving" && tripMilesStraight < 10;

    // COASTAL TOLERANCE OVERRIDE: a genuinely coastal corridor (both endpoints
    // near CA-1) is the one case where "balanced" tolerance refuses to commit
    // to the ocean road — it snaps back inland because the coast is a few
    // minutes longer. The landing demo proves running at SCENIC tolerance
    // surfaces the coastal route cleanly (no monster), so for coastal driving
    // trips we behave exactly like the demo. Non-coastal trips are unchanged.
    const isCoastalCorridor =
      effectiveMode === "driving" &&
      primaryWaypoints.length === 0 &&
      pchWaypointsForCorridor(startCoords, dest.coords).length > 0;
    // CURATED GREAT-ROAD OVERRIDE: same idea as the coast, generalized. If the
    // trip passes near a hand-curated road (Round Hill Road, etc.), we run at
    // scenic tolerance and inject a waypoint through it below — otherwise the
    // engine never leaves the direct line to find it. See scenicRoads.ts.
    // COAST OUTRANKS INLAND. On a genuinely coastal corridor (SF → Half Moon
    // Bay), the coast is rank #1 — period. Injecting inland curated roads
    // (Skyline Blvd, Cañada Rd) here lets them out-compete the coastal route
    // for the Pleasant slot, which is the regression Morgan caught. So when
    // the coastal override is active, we DON'T gather inland curated matches:
    // the coastal-pch route runs unopposed and wins Pleasant on its ocean
    // premium. Non-coastal trips are unchanged.
    const scenicRoadMatches =
      primaryWaypoints.length === 0 && !isCoastalCorridor
        ? scenicRoadsForCorridor(startCoords, dest.coords, { mode: effectiveMode, maxRoads: 3 })
        : [];
    const isCuratedRoadCorridor = scenicRoadMatches.length > 0;
    // The scenic-tolerance override only changes driving behavior; walking and
    // biking just get the curated street injected into the pool below, so we
    // don't widen their time budget.
    const effectiveTolerance =
      isCoastalCorridor || (isCuratedRoadCorridor && effectiveMode === "driving")
        ? "scenic"
        : profile.detourTolerance;

    const noHwyOverrideSec =
      effectiveTolerance === "fast"     ? 0 :
      shortDrive                        ? Number.POSITIVE_INFINITY :
      effectiveTolerance === "scenic"   ? Number.POSITIVE_INFINITY :
      effectiveTolerance === "balanced" ? 12 * 60 :
                                          5 * 60;
    const isScenicTol = effectiveTolerance === "scenic";
    // Pleasant + Scenic both exclude motorway on driving now (#116).
    // Real-drive bug: Tarrytown → Greenwich on Pleasant ran the entire
    // Taconic State Parkway because we only told Mapbox to skip motorways
    // on Scenic. Pleasant = "the nicer of two paths" implies "not the
    // 55mph parkway when backroads exist." If no motorway-free
    // alternative exists, Mapbox will return motorway anyway (the
    // exclude is a hint, not a hard ban).
    const baseExcludes =
      effectiveMode === "driving" && effectiveTolerance !== "fast"
        ? (["motorway"] as const)
        : ([] as const);
    // (#144) Three-cards-always candidate generation. ONE bundled Mapbox
    // call returns 3 near-duplicate alternatives that all honor the same
    // `exclude=motorway` (set by the legacy tolerance setting). That left
    // the Direct/Pleasant/Scenic slots picking from a single no-motorway
    // pool — Direct would surface a loopy residential route because
    // Bedford Rd direct never made it into the pool. Real-drive bug:
    // Sleepy Hollow → Tarrytown showed 3 cards with the SAME polyline
    // because the three slots all fell back to the engine's fastest
    // among the (already filtered) alternatives.
    //
    // Real-world assumption: Mapbox returns genuinely different polylines
    // when you ask it different questions. One call with no filters
    // surfaces the literal "drive there directly" route; a parallel call
    // with `exclude=motorway` surfaces residential alternatives; a call
    // anchored on a scenic POI surfaces detours through that POI. Merging
    // all three pools gives classifyThreeCards enough diversity that the
    // Direct/Pleasant/Scenic slots end up with visibly different routes
    // on the map. (Scenic anchors are filled in by the background
    // enrichment pass; the fast path runs with empty anchors so initial
    // render isn't gated on an Overpass call.)
    //
    // Walking/biking + any trip with intermediate waypoints fall back to
    // the legacy single-call path because (a) Mapbox doesn't accept
    // `exclude=motorway` for those profiles, and (b) `alternatives=true`
    // is rejected when intermediate waypoints are present.
    // FIRST-PAINT FETCH (Morgan, sub-2s pass). Everything that feeds the first
    // cards is ONE batch of concurrent Mapbox calls: the base pool (Direct +
    // motorway-excluded Pleasant + scenic) AND the coastal/curated detours.
    // Build the detour fetches first (they only need the endpoints + the
    // already-computed scenicRoadMatches), then fire them ALONGSIDE the pool so
    // wall-clock is ~one Mapbox round-trip instead of pool-then-detours.
    // COASTAL EXCEPTION (#ocean): on a genuinely coastal corridor we route
    // THROUGH Pacifica/Devil's Slide so CA-1 wins the Pleasant slot. CURATED:
    // route through a curated great road so the engine commits to it.
    const injectionFetches: Promise<{ src: string; routes: RawRoute[] }>[] = [];
    if (primaryWaypoints.length === 0) {
      if (effectiveMode === "driving") {
        // HARD PULL TO THE WATER (Morgan, Aug 1: "we need a hard pull to the
        // coast of the lakes").
        //
        // One anchor is plenty for SF → Half Moon Bay: 28 miles, and CA-1 runs
        // nearly straight, so a single point on the highway holds the whole
        // route. It is nowhere near enough for Holland → Traverse City. That
        // coast bows ~50km west of the direct line over 170 miles, so ONE
        // anchor near Ludington can be satisfied by running US-131 inland,
        // darting west to touch Ludington, and going straight back — which is
        // both ugly and not what anyone means by driving up the lake.
        //
        // Scale the anchors with the length of the run (~one per 45 miles, max
        // 5) so the route is pinned to the shore the entire way instead of
        // being tugged toward it once. Short coastal hops still get exactly
        // one, so CA behaviour on the trips it was tuned for is unchanged.
        // DENSITY IS WHAT "HUG" MEANS (Morgan, Aug 2 — Holland→TC came back
        // riding US-31, the expressway a few miles inland, and the coastal
        // candidate was so similar to the generic route that dedupe swallowed
        // it and the Scenic card vanished). One anchor every 10 miles — her
        // number — pins the route to the shoreline road itself between towns.
        // Capped at 20: Mapbox's Directions API allows 23 intermediate
        // waypoints, and past ~20 the URL and the risk of one bad snap killing
        // the request both grow faster than the fidelity does. The injection
        // fetch is catch-wrapped, so a failure costs the candidate, not the
        // trip.
        // EVERY CURATED SAMPLE, NO DOWNSAMPLING (Morgan, Aug 3 — Portland→SF
        // came back riding inland 101/I-5 with comb-tooth spurs poking out to
        // the coast). Root cause: 20 anchors over 630 miles = ~35 mi gaps,
        // and wherever TWO parallel roads exist (101 inland, CA-1 on the
        // coast) the router rides the fast spine between sparse anchors and
        // darts out to touch each one. Michigan never combs because 138 mi /
        // 12 anchors leaves no room to cheat — density is what "hug" means.
        // So: use ALL curated samples between the endpoints. Between ADJACENT
        // samples on the same coast road, the shortest path IS the road —
        // combs become geometrically impossible. Mapbox's 25-coordinate limit
        // is handled below by fetching the thread in stitched legs.
        const coastalWps = pchWaypointsForCorridor(startCoords, dest.coords, {
          maxWaypoints: 60,
        });
        // Diagnostic (Morgan, Aug 1): three straight attempts at this were
        // debugged from screenshots, which cannot distinguish "the coastal
        // candidate was never built" from "it was built and then lost a card
        // slot". Those need opposite fixes. Say which, out loud.
        console.log(
          `[coastal] anchors=${coastalWps.length} trip=${Math.round(tripMilesStraight || 0)}mi`,
        );
        if (coastalWps.length > 0) {
          // NO motorway exclusion on the coastal thread (Morgan, Aug 3: "we
          // just want to be on route 101 the whole way"). US-101 is
          // expressway-grade around Fortuna/Eureka; banning motorway forced
          // the router OFF 101 at every such stretch and back on after — the
          // loop-de-loops. CLAUDE.md rule 2 already exempts epic coastal
          // highways: there, the highway IS the attraction. The anchors sit
          // ON the coast road, so allowing motorway just lets the route BE
          // that road. (Michigan's shore anchors are ~10 mi apart on
          // non-motorway shore roads — the shortest link between them is
          // still the shore road, so lakeshore behavior is unchanged.)
          // ≤20 vias fits one Mapbox request (23-via limit, with margin):
          // the original single call — Michigan and every short coastal trip
          // are byte-identical to before. Longer threads are fetched as
          // stitched LEGS, split at curated samples, so every sample is a
          // via in some leg and no gap is wide enough to comb.
          const LEG_MAX_VIAS = 18;
          if (coastalWps.length <= 20) {
            injectionFetches.push(
              // silentVias: curated samples SHAPE the road, they are not places
              // the driver asked to stop. See the scenic-road injection below.
              fetchRoutes(startCoords, dest.coords, effectiveMode, coastalWps, [], Number.POSITIVE_INFINITY, true)
                .then((routes) => {
                  console.log(`[coastal] injected routes=${routes.length}`);
                  return { src: "coastal-pch", routes };
                })
                .catch(() => {
                  console.log("[coastal] injection FAILED (fetch error)");
                  return { src: "coastal-pch", routes: [] as RawRoute[] };
                }),
            );
          } else {
            const legCount = Math.ceil(coastalWps.length / LEG_MAX_VIAS);
            const per = Math.ceil(coastalWps.length / legCount);
            const legs: { a: LngLat; b: LngLat; vias: LngLat[] }[] = [];
            let legStart: LngLat = startCoords;
            for (let i = 0; i < coastalWps.length; i += per) {
              const chunk = coastalWps.slice(i, i + per);
              const isLast = i + per >= coastalWps.length;
              // The last via of each non-final chunk becomes the next leg's
              // start — a curated sample, so the seam sits ON the coast road.
              const end: LngLat = isLast ? dest.coords : chunk[chunk.length - 1];
              legs.push({ a: legStart, b: end, vias: isLast ? chunk : chunk.slice(0, -1) });
              legStart = end;
            }
            injectionFetches.push(
              Promise.all(
                legs.map((l) => fetchRoutes(l.a, l.b, effectiveMode, l.vias, [], Number.POSITIVE_INFINITY, true)),
              )
                .then((legRoutes) => {
                  const firsts = legRoutes.map((rs) => rs[0]);
                  if (firsts.some((r) => !r)) throw new Error("a coastal leg returned no route");
                  const sum = (f: (r: RawRoute) => number) => firsts.reduce((s, r) => s + f(r), 0);
                  const roadClassMeters: Record<string, number> = {};
                  for (const r of firsts) {
                    for (const [k, v] of Object.entries(r.roadClassMeters ?? {})) {
                      roadClassMeters[k] = (roadClassMeters[k] ?? 0) + v;
                    }
                  }
                  const stitched: RawRoute = {
                    ...firsts[0],
                    coordinates: firsts.flatMap((r, i) => (i === 0 ? r.coordinates : r.coordinates.slice(1))),
                    navCoordinates: firsts.some((r) => r.navCoordinates)
                      ? firsts.flatMap((r, i) => {
                          const c = r.navCoordinates ?? r.coordinates;
                          return i === 0 ? c : c.slice(1);
                        })
                      : undefined,
                    distance: sum((r) => r.distance),
                    duration: sum((r) => r.duration),
                    steps: firsts.flatMap((r) => r.steps),
                    motorwayMeters: sum((r) => r.motorwayMeters ?? 0),
                    highTrafficMeters: sum((r) => r.highTrafficMeters ?? 0),
                    tunnelMeters: sum((r) => r.tunnelMeters ?? 0),
                    tollMeters: sum((r) => r.tollMeters ?? 0),
                    epicHighwayMeters: sum((r) => r.epicHighwayMeters ?? 0),
                    epicHighwayLabels: [...new Set(firsts.flatMap((r) => r.epicHighwayLabels ?? []))],
                    roadClassMeters,
                  };
                  console.log(`[coastal] injected routes=1 (stitched from ${firsts.length} legs, ${coastalWps.length} vias)`);
                  return { src: "coastal-pch", routes: [stitched] };
                })
                .catch(() => {
                  console.log("[coastal] injection FAILED (a stitched leg errored)");
                  return { src: "coastal-pch", routes: [] as RawRoute[] };
                }),
            );
          }
        }
      }
      if (scenicRoadMatches.length > 0) {
        const curatedExcludes: RouteExclude[] = effectiveMode === "driving" ? ["motorway"] : [];
        for (const m of scenicRoadMatches) {
          if (m.waypoints.length === 0) continue;
          injectionFetches.push(
            // A CURATED SAMPLE IS NOT A STOP (Morgan, Aug 9 — Briarcliff →
            // Rhinebeck: "no i do not want STOP there ... it's not correct").
            // The turn list showed "54 steps · 3 stops" and three rows reading
            // "STOP — Your destination is on the right", on a trip where she
            // added no stops at all. Mapbox splits its response into one leg
            // per waypoint, and routing.ts marks the last step of every
            // non-final leg as an arrival at a user stop — an assumption that
            // was true when the only waypoints came from Add-a-stop. Curated
            // injection threads the route through the road's own samples, so
            // rule 14's density ("use EVERY curated sample") turned directly
            // into phantom stops: the more faithfully we hug the road, the
            // more arrivals the driver was told about.
            //
            // silentVias sends waypoints=0;N-1, so the samples steer the route
            // without splitting it. The mechanism already existed for Loops
            // ("vias are shaping points, not stops"); it was simply never
            // wired to curated roads. Provenance decides, as in rule 16: the
            // engine's own geometry is silent, a stop the driver ASKED for
            // keeps its leg and its arrival cue (Index.tsx applyDetourPicks
            // and the nav re-routes are deliberately left alone).
            fetchRoutes(startCoords, dest.coords, effectiveMode, m.waypoints, curatedExcludes, Number.POSITIVE_INFINITY, true)
              .then((routes) => {
                // Confession (rule 17, added diagnosing #63 — this injection
                // used to fail in silence): what each curated match produced.
                console.log(`[scenic-road] injection "${m.road.name}": routes=${routes.length}`);
                return { src: "scenic-road", routes };
              })
              .catch(() => {
                console.log(`[scenic-road] injection "${m.road.name}" FAILED (fetch error)`);
                return { src: "scenic-road", routes: [] as RawRoute[] };
              }),
          );
        }
      }
    }

    // VALHALLA — BACKGROUND ONLY (Morgan). The graph-level avoid-highways engine
    // finds back-road alternatives Mapbox can't surface on corridors like
    // Boston→Concord (Route 2), where Mapbox's calm pool just collapses onto the
    // direct line. It's ~10-20s, so it must NEVER gate first paint — we kick it
    // off here (parallel with the Mapbox pool) but only AWAIT it in the
    // background pass below, where it folds a calmer route in a few seconds
    // after the Direct card is already on screen. Fast first paint AND back-road
    // coverage.
    const valhallaBgPromise: Promise<RawRoute[]> =
      effectiveMode === "driving" && primaryWaypoints.length === 0
        ? (async () => {
            try {
              const [{ fetchValhallaRoutes }, { raceTimeout }] = await Promise.all([
                import("@/api/valhalla"),
                import("@/lib/net"),
              ]);
              // CEILING, ALWAYS (Morgan, Aug 6: "taking too long here to
              // paris"). These calls had NO client timeout — on a 900 km
              // trip Valhalla's avoid-everything costing can chew for ages
              // and the background pass waited politely forever. 20s each;
              // a late Valhalla is a skipped Valhalla, never a stall.
              const [adv, bal] = await Promise.all([
                raceTimeout(
                  fetchValhallaRoutes(startCoords, dest.coords, [], "adventure").catch(() => [] as RawRoute[]),
                  20_000,
                ).then((r) => r ?? ([] as RawRoute[])),
                raceTimeout(
                  fetchValhallaRoutes(startCoords, dest.coords, [], "balanced").catch(() => [] as RawRoute[]),
                  20_000,
                ).then((r) => r ?? ([] as RawRoute[])),
              ]);
              tagRoutesSource(adv, "valhalla-adventure");
              tagRoutesSource(bal, "valhalla-balanced");
              return [...adv, ...bal];
            } catch {
              return [] as RawRoute[];
            }
          })()
        : Promise.resolve([]);

    // ALONG-CORRIDOR scenic discovery (background, driving only). For a long
    // trip whose nicer way is a MID-trip detour — the Maine coast on a
    // Brunswick→Somerville run, a river road on a Hudson trip — Mapbox only ever
    // returns highway variants and the endpoint matcher (scenicRoadMatches
    // above) finds nothing, so the engine reports "no calmer route." Here we
    // find a curated scenic road that sits along the route axis and route a
    // candidate through it, folding it into the pool a few seconds after first
    // paint (never gating it). The Scenic card then carries it as the longer,
    // beautiful option. Only fires when the endpoint matcher came up empty, so
    // it doesn't fight the near-corridor injection on shorter trips.
    // Length cap: scenic-corridor discovery runs Overpass coastline/scenic
    // queries over a bbox spanning the whole trip. On a transcontinental trip
    // (Brunswick → LAX) that bbox is the entire continent — the query returns a
    // colossal payload and locks the main thread parsing it ("freezes" on
    // "Finding your most pleasant route"). Beyond a few hundred miles the
    // "scenic detour" idea also stops making sense, so we simply skip it. The
    // engine still returns Direct + any in-budget Pleasant route.
    // MORGAN'S GEOMETRY (Aug 2, her spec verbatim): "take the length of the
    // fastest route and go half that in either direction down the length of
    // the route — if there is water, the route hugs the water."
    //
    // So: WATER FIRST, searched in a corridor that scales with the trip
    // (half the straight-line length either side, sanity-capped at 150km so a
    // cross-country trip doesn't claim every lake on the continent), from
    // REAL map data — no hand-placed samples to forget a shoreline. This ran
    // before, but (a) only when curated roads found nothing, so Michigan's
    // curated M-22 entries ironically suppressed the actual water thread, and
    // (b) it was disabled entirely on recognized coastal corridors, the one
    // place it was most needed. Both gates removed.
    const MAX_SCENIC_CORRIDOR_MILES = 700; // was 400 — Portland→SF is 536
    const scenicCorridorBgPromise: Promise<RawRoute[]> =
      effectiveMode === "driving" &&
      primaryWaypoints.length === 0 &&
      tripMilesStraight <= MAX_SCENIC_CORRIDOR_MILES
        ? (async () => {
            // Discovery waits for pass 1 — see the discoveryGate comment above.
            await discoveryGate;
            try {
              const { fetchWatersideCorridorVias, fetchScenicCorridorVias } = await import("@/api/osm");
              const tripKm = (tripMilesStraight || 0) * 1.60934;
              // Morgan's spec, verbatim (Aug 2, restated twice so it stuck):
              // take the WHOLE length of the trip, turn it perpendicular, run
              // it down the route — half the length to each side IS the search
              // area. No 150km cap (that was an invented hedge that cut
              // Portland→SF's mandated 430km reach to a third). The 700-mile
              // trip ceiling above is the only bound; past that the box would
              // be a continent.
              //
              // THE FLOOR WAS NOT PART OF THE SPEC (Aug 21). `tripKm / 2` is
              // Morgan's rule and it is untouched. `Math.max(35, ...)` was an
              // added minimum, and on short trips it is the whole cost of the
              // scenery layer: a 10-mile trip searched 35 km to EACH side, a
              // box roughly 36x larger in area than anything the time budget
              // could ever route to.
              //
              // Check the arithmetic against our own budget. A 10-mile trip is
              // about 20 minutes, so pleasantBudgetSeconds allows min(75%, 40)
              // = 15 minutes extra. At back-road pace that is ~12 km of extra
              // DISTANCE, so a via can sit at most ~6 km off the direct line
              // and still be reachable. We were reading water 35 km away and
              // then discarding it on the client. The minutes rule is tighter
              // still: +5.5 minutes, about 2 km off-axis.
              //
              // Measured cost of the difference, cold, same function: a
              // 42x33 km box read 7,292 pages in 11.7s; an 11x10 km box read
              // 2,414 pages in 2.5s. The 8s anon timeout sits between them,
              // which is why half of scenic_features_in_bbox calls were
              // returning 500 and drivers were getting no scenery at all.
              //
              // So: floor lowered 35 -> 6. Long trips are UNCHANGED --
              // Portland->SF still gets its full 215 km reach from tripKm / 2.
              // Only trips under ~12 km see a smaller box than before, and
              // what they lose is land they could never have driven to.
              const waterCorridorKm = Math.max(6, tripKm / 2);
              // WATER IS ONE SIGNAL, NOT THE SIGNAL (Morgan, Aug 21:
              // "i think we are placing way too much emphasis on water").
              //
              // Measured over 65 route variants: each extra water via cost
              // ~9 minutes and LOWERED the scenery gain.
              //   1 via  -> +8.8 min, 0.96x land per mile
              //   5 vias -> +45.6 min, 0.75x
              // Every extra waypoint is one more leg the router must connect,
              // and it connects it with whatever road happens to be there.
              //
              // Worse, the picker keeps the water NEAREST THE STRAIGHT LINE
              // (`pt.off < prev.off`). On a long trip that is a drainage
              // ditch, not the coast — the exact opposite of what a long
              // drive should reach for.
              //
              // Morgan's rule: "for trips not close to obvious water
              // landmarks and of shorter lengths - longer lengths, get to
              // the coast, shorter dont unless it is easy."
              //
              //   < 40 km  : 1 via, and ONLY if it is within 3 km of the
              //              line. That is what "unless it is easy" means:
              //              on the way, not a detour.
              //   40-150 km: 1 via, normal corridor.
              //   > 150 km : 2 vias, normal corridor.
              //
              // "Get to the coast" is NOT done here and cannot be: this
              // source reads scenic_features_in_bbox, which returns no
              // size_m, so it has no way to tell an estuary from a ditch.
              // Reaching for the biggest water is already the wide net's
              // job (fetchWideScenicViasFromLayer bins by size, not by
              // distance) — it is the thing that needs taming, not building.
              const waterViaMax = tripKm > 150 ? 2 : 1;
              const waterEasyOnlyKm = tripKm < 40 ? 3 : undefined;
              // ONE SLOW SIGNAL MUST NOT STARVE THE OTHERS (Morgan, Aug 9).
              //
              // These three sources used to run in strict sequence: water, and
              // only if water came back empty, curated, and only then
              // scenic-tagged. That is the right PRIORITY and it was the wrong
              // CONTROL FLOW, because `await` made a slow source cost the fast
              // ones their time rather than just their turn.
              //
              // The evidence that this is availability and not geography:
              // yesterday a run logged `[water] corridor=474km points=0` and
              // still folded in TWO scenic-corridor routes. Water returning
              // nothing was survivable. Water burning ~20s before returning
              // nothing is not — the discovery phase has a hard deadline above,
              // and today the `around.water:250` query spends the entire budget
              // failing (`raw label=waterside elements=0 ms=20001`, measured on
              // our own server). So `scenic corridor folded in (0 routes)` on
              // every run all day is the fallback never getting a turn.
              //
              // Both network sources now run TOGETHER on their own short
              // budgets. Priority below is unchanged; only the waiting is.
              const DISCOVERY_BUDGET_MS = 6_000;
              const withBudget = async <T,>(
                work: Promise<T>,
                empty: T,
              ): Promise<{ value: T; status: string }> => {
                try {
                  return await Promise.race([
                    work.then((value) => ({ value, status: "ok" })),
                    new Promise<{ value: T; status: string }>((resolve) =>
                      setTimeout(
                        () => resolve({ value: empty, status: `timeout@${DISCOVERY_BUDGET_MS}ms` }),
                        DISCOVERY_BUDGET_MS,
                      ),
                    ),
                  ]);
                } catch (err) {
                  return { value: empty, status: `error:${String(err).slice(0, 40)}` };
                }
              };
              // OUR OWN DATA FIRST, Overpass only where we have none.
              //
              // The scenic layer answers this from an indexed bbox lookup in
              // Postgres instead of a live Overpass query that has been
              // spending its entire 20s budget to return nothing (measured on
              // our own server: `raw label=waterside elements=0 ms=20001`).
              //
              // The fallback is deliberate and TEMPORARY. Only imported
              // regions can answer, so everywhere else must still ask
              // Overpass or scenic discovery would silently switch off for
              // the whole world outside New York. What makes this safe rather
              // than a hedge is that the layer distinguishes "no water here"
              // from "never imported" — an empty ANSWER is used, an
              // UNMEASURED answer falls through. Overpass could never tell
              // those apart, which is the bug (rule 20).
              //
              // Once coverage is broad this fallback comes out and an
              // uncovered corridor simply scores no scenery.
              const watersideVias = async (): Promise<LngLat[]> => {
                const { fetchWatersideCorridorViasFromLayer } = await import("@/api/scenicLayer");
                const layer = await fetchWatersideCorridorViasFromLayer(startCoords, dest.coords, {
                  max: waterViaMax,
                  corridorKm: waterCorridorKm,
                  easyOnlyKm: waterEasyOnlyKm,
                });
                if (layer.covered) {
                  console.log(
                    `[water] source=layer region=${layer.region} features=${layer.features} ` +
                      `points=${layer.vias.length} ms=${layer.ms}` +
                      (layer.features === 0 ? " (covered region — this is a REAL zero)" : ""),
                  );
                  return layer.vias;
                }
                console.log(
                  `[water] source=overpass — corridor is outside every imported region ` +
                    `(UNMEASURED, not empty) — falling back after ${layer.ms}ms`,
                );
                return fetchWatersideCorridorVias(startCoords, dest.coords, {
                  max: waterViaMax,
                  corridorKm: waterCorridorKm,
                });
              };
              // GREEN + LANDMARKS — the half of the layer nothing read.
              //
              // Overpass never had a cheap way to ask "where are the parks and
              // viewpoints along this line", so scenic discovery was water and
              // scenic-tagged roads only. The layer already holds it: on the
              // first two regions, 454,607 green features and 40,228 scenic
              // points, indexed and unqueried. This is a NEW source, not a
              // replacement — it runs on the same budget as the others and
              // ranks below water in the priority order below, unchanged.
              const greenVias = async (): Promise<LngLat[]> => {
                const { fetchGreenCorridorViasFromLayer } = await import("@/api/scenicLayer");
                const g = await fetchGreenCorridorViasFromLayer(startCoords, dest.coords, {
                  max: 2,
                  corridorKm: Math.max(4, Math.min(60, waterCorridorKm / 2)),
                });
                if (!g.covered) {
                  console.log("[green] corridor outside every imported region — UNMEASURED, no green source");
                  return [];
                }
                console.log(
                  `[green] region=${g.region} features=${g.features} points=${g.vias.length} ms=${g.ms}` +
                    (g.features === 0 ? " (covered region — this is a REAL zero)" : ""),
                );
                return g.vias;
              };
              // 5. THE WIDE NET — Scenic is not on the clock (Morgan, repeatedly).
              //
              // Every source above searches the SAME corridor Pleasant does, so
              // Scenic has never actually had more reach than Pleasant — just
              // the same box ranked differently. This one goes properly wide and
              // pays for it by asking only for features 2 km and over, which is
              // what a long detour is for anyway.
              //
              // The 150 km cap is measured, not invented: a 170x165 km box costs
              // 6.7s cold through scenic_big_features_in_bbox, and the anon
              // timeout is 8s. Wider than this and Scenic goes back to getting
              // nothing at all.
              const wideScenicKm = Math.min(150, Math.max(60, tripKm * 1.5));
              const wideScenicVias = async (): Promise<LngLat[]> => {
                const { fetchWideScenicViasFromLayer } = await import("@/api/scenicLayer");
                const w = await fetchWideScenicViasFromLayer(startCoords, dest.coords, {
                  max: 2,
                  corridorKm: wideScenicKm,
                });
                if (!w.covered) {
                  console.log("[scenic-wide] outside imported coverage — UNMEASURED, no wide source");
                  return [];
                }
                return w.vias;
              };
              const [waterRes, taggedRes, greenRes, wideRes] = await Promise.all([
                // The budget covers layer + any fallback. The layer leg is an
                // indexed lookup, so a fallback still gets nearly the whole
                // window; a covered corridor should finish in well under it.
                withBudget(watersideVias(), [] as LngLat[]),
                withBudget(
                  fetchScenicCorridorVias(startCoords, dest.coords, { max: 2 }),
                  [] as LngLat[],
                ),
                withBudget(greenVias(), [] as LngLat[]),
                withBudget(wideScenicVias(), [] as LngLat[]),
              ]);
              const waterside = waterRes.value;
              // 1. WATER, per the geometry above.
              let vias: LngLat[][] = waterside.length >= 2 ? [waterside] : [];
              console.log(`[water] corridor=${Math.round(waterCorridorKm)}km points=${waterside.length}`);
              // 2. Curated scenic roads along the axis (local, no network).
              const curatedVias =
                vias.length === 0
                  ? scenicRoadsAlongCorridor(startCoords, dest.coords, {
                      mode: "driving",
                      maxRoads: 2,
                    }).map((m) => m.waypoints)
                  : [];
              if (vias.length === 0) vias = curatedVias;
              // 3. Scenic-tagged roads from OSM — already fetched above, so a
              //    dead water query can no longer cost this its turn.
              if (vias.length === 0) vias = taggedRes.value.map((v) => [v]);
              // 4. GREEN + LANDMARKS from our own layer. LAST on purpose: the
              //    three sources above are stronger scenic claims, and placing
              //    green beneath them means this can only ADD a candidate on a
              //    trip that would otherwise have produced none — it can never
              //    displace a water thread or a curated road. On the trips that
              //    have been logging `scenic corridor folded in (0 routes)` all
              //    day, this is the source that finally has something to say.
              if (vias.length === 0) vias = greenRes.value.map((v) => [v]);
              // 5. THE WIDE NET — APPENDED, never substituted. A trip that
              //    already found water or a curated road keeps it; this can only
              //    put a far-off lake or forest on the table that none of the
              //    four narrow sources was ever going to see. That is the whole
              //    difference between the Scenic card and the Pleasant one.
              if (wideRes.value.length >= 1) vias = [...vias, wideRes.value];
              // THE CONFESSION (rule 17). `scenic corridor folded in (0 routes)`
              // never said which of the three sources came up empty, or whether
              // one of them simply never ran. This line does.
              console.log(
                `[scenic-discovery] water=${waterside.length}/${waterRes.status} ` +
                  `curated=${curatedVias.length} ` +
                  `tagged=${taggedRes.value.length}/${taggedRes.status} ` +
                  `green=${greenRes.value.length}/${greenRes.status} ` +
                  `wide=${wideRes.value.length}/${wideRes.status}@${Math.round(wideScenicKm)}km ` +
                  `→ using ${vias.length} via-set(s)`,
              );
              if (vias.length === 0) return [] as RawRoute[];
              const fetched = await Promise.all(
                vias.map(async (waypoints) => {
                  try {
                    const routes = await fetchBaseRoutesWithFallback({
                      startCoords,
                      destCoords: dest.coords,
                      mode: "driving",
                      primaryWaypoints: waypoints,
                      detourTolerance: "scenic",
                      baseExcludes: ["motorway"] as const,
                      noHwyOverrideSec: Number.POSITIVE_INFINITY,
                    });
                    tagRoutesSource(routes, "scenic-corridor");
                    return routes;
                  } catch {
                    return [] as RawRoute[];
                  }
                }),
              );
              return fetched.flat();
            } catch {
              return [] as RawRoute[];
            }
          })()
        : Promise.resolve([]);

    // CHARMING-TOWNS road-trip pass (Morgan: "for these classic cross-country
    // road trips… going through great towns… cute towns"). Unlike the scenic-
    // corridor pass above, this reads the CURATED charming-towns registry — pure
    // arithmetic, NO Overpass — so it's safe at ANY distance, including the cross-
    // country trips where scenic-corridor is gated off for the freeze. We find the
    // cute towns sitting along the trip axis and route a single candidate THROUGH
    // them (fast roads between towns, threaded into each one), folded into the pool
    // after first paint and surfaced as the scenic / pleasant pick. Only for
    // driving, point-to-point, at road-trip scale (so a short hop doesn't detour).
    const TOWNS_MIN_MILES = 120;
    const townsCorridorBgPromise: Promise<RawRoute[]> =
      effectiveMode === "driving" &&
      primaryWaypoints.length === 0 &&
      tripMilesStraight >= TOWNS_MIN_MILES
        ? (async () => {
            // Discovery waits for pass 1 — see the discoveryGate comment above.
            await discoveryGate;
            try {
              // COASTAL BEND (Morgan: "hug the coast… a real coastal road trip,
              // not an I-95 clone with 2 detours"). A coastal trip is a deliberate
              // eastward swing OFF the inland interstate line, so coastal towns sit
              // ~200km off it — too far for the normal towns matcher. When ≥2
              // coastal towns lie along the (wide) corridor, thread the route
              // through THEM (which pulls it to the shore) and weave WATERSIDE
              // roads in between, per leg. Per-leg town→town bboxes stay regional,
              // so each Overpass query is bounded — no continental freeze; legs
              // ≥250km just take the fast road to the next town. Otherwise fall
              // back to the inland charming-towns route.
              const coastal = isCoastalTownTrip(startCoords, dest.coords);
              const towns = coastal
                ? coastalTownsAlongCorridor(startCoords, dest.coords, { corridorKm: 280, max: 6 })
                : townsAlongCorridor(startCoords, dest.coords, { corridorKm: 150, max: 4 });
              if (towns.length === 0) return [] as RawRoute[];
              let waypoints: LngLat[] = towns.map((t) => t.coord);
              if (coastal) {
                try {
                  const { fetchWatersideCorridorVias } = await import("@/api/osm");
                  const { haversine } = await import("@/utils/polyline");
                  const pts: LngLat[] = [startCoords, ...towns.map((t) => t.coord), dest.coords];
                  const woven: LngLat[] = [];
                  for (let i = 0; i < pts.length - 1; i++) {
                    const a = pts[i], b = pts[i + 1];
                    if (haversine(a, b) < 250_000) {
                      const vias = await fetchWatersideCorridorVias(a, b, { corridorKm: 35, max: 2 });
                      woven.push(...vias);
                    }
                    if (i < pts.length - 2) woven.push(b); // re-insert the town (not the final dest)
                    if (woven.length >= 22) break; // Mapbox waypoint cap guard
                  }
                  // Only adopt if we actually added shore points beyond the towns.
                  const townKeys = new Set(towns.map((t) => `${t.coord[0]},${t.coord[1]}`));
                  if (woven.some((p) => !townKeys.has(`${p[0]},${p[1]}`))) waypoints = woven;
                } catch { /* keep plain town waypoints */ }
              }
              if (isCancelled()) return [] as RawRoute[];
              const routes = await fetchBaseRoutesWithFallback({
                startCoords,
                destCoords: dest.coords,
                mode: "driving",
                primaryWaypoints: waypoints,
                // "fast" (NOT "scenic"): "scenic" routes via Valhalla's
                // max-backroads "adventure" engine, which hugs slow roads the
                // whole way (NY→FL came out at 37.5h). The town WAYPOINTS already
                // force the route into each town; between them it should take fast
                // roads. So route normally through the waypoints — a sensible
                // +2-3h detour, not a near-doubling.
                detourTolerance: "fast",
                baseExcludes: [] as const,
                noHwyOverrideSec: 0,
              });
              // Tagged "scenic-corridor" so it reuses the whole battle-tested
              // scenic-card path (corridorPick, dedup survival, enrichment); the
              // townNames field carries the distinct "through Charleston and
              // Savannah" labeling.
              tagRoutesSource(routes, "scenic-corridor");
              const names = towns.map((t) => t.name);
              for (const r of routes) r.townNames = names;
              return routes;
            } catch {
              return [] as RawRoute[];
            }
          })()
        : Promise.resolve([]);

    // WALK / CITY-BIKE ADVENTURE discovery (Morgan: "we should be able to have
    // fun adventures walking in the city and now it's mostly direct"). The
    // pedestrian equivalent of the driving scenic-corridor pass: find nearby
    // parks, waterfront, pedestrian squares and landmarks, thread the walk
    // THROUGH them, and surface it as the adventure card. Background, never gates
    // first paint. Only for walking/biking on a point-to-point trip.
    const walkAdventureBgPromise: Promise<RawRoute[]> =
      (effectiveMode === "walking" || effectiveMode === "biking") &&
      primaryWaypoints.length === 0
        ? (async () => {
            // Discovery waits for pass 1 — see the discoveryGate comment above.
            await discoveryGate;
            try {
              const { fetchWalkAdventureVias, fetchWalkLinearScenicVias } = await import("@/api/osm");
              const { walkAdventureBudget } = await import("@/utils/adventureBudget");
              const tripKm = (tripMilesStraight || 0) * 1.60934;
              const { corridorKm } = walkAdventureBudget(
                tripKm,
                effectiveMode === "biking" ? "biking" : "walking",
              );
              // PREFER a LINEAR scenic feature the walk can FOLLOW (greenway,
              // riverside promenade, esplanade) — an ordered string of points down
              // it threads CLEANLY, no out-and-back loops/spurs. Only fall back to
              // detouring-to-point-features when no linear feature runs along the
              // corridor.
              // NOTE: the park-trail injection (fetchWalkParkTrailVias) was pulled
              // out — a single park footpath the route only touches partway
              // produced an out-and-back spur into the park (Morgan). Following a
              // park trail end-to-end needs the trail to actually span the
              // corridor, which the linear-scenic pass already requires; the
              // park-trail pass didn't, so it made loops. Revisit with a proper
              // span/continuity guard before re-enabling.
              let vias = await fetchWalkLinearScenicVias(startCoords, dest.coords, {
                corridorKm: Math.max(corridorKm, 1.2),
              });
              if (vias.length === 0) {
                vias = await fetchWalkAdventureVias(startCoords, dest.coords, {
                  corridorKm,
                  // At most 2 point features — more zig-zags into the
                  // walk-around-a-block loops the coherence gate then rejects.
                  max: 2,
                });
              }
              if (vias.length === 0) return [] as RawRoute[];
              // Thread ONE candidate through all the discovered features in order
              // — a sustained wander, not a single nip past a park.
              try {
                const routes = await fetchBaseRoutesWithFallback({
                  startCoords,
                  destCoords: dest.coords,
                  mode: effectiveMode,
                  primaryWaypoints: vias,
                  detourTolerance: "scenic",
                  baseExcludes: [],
                  noHwyOverrideSec: Number.POSITIVE_INFINITY,
                });
                tagRoutesSource(routes, "scenic-corridor");
                return routes;
              } catch {
                return [] as RawRoute[];
              }
            } catch {
              return [] as RawRoute[];
            }
          })()
        : Promise.resolve([]);

    // "NICE CITY STREETS" VARIETY WALK (Morgan: "another route just for variety
    // along other nice city streets"). A SECOND, distinct walk candidate: instead
    // of following one waterfront feature, it stitches together a sequence of
    // curated pretty blocks (greatWalks) that lie along the corridor. Tagged
    // scenic-corridor like the waterfront walk, but geometrically different — so
    // the classifier surfaces BOTH (waterfront → one card, charming streets →
    // another). Walking only; the curated segments are pedestrian-scale.
    const walkVarietyBgPromise: Promise<RawRoute[]> =
      effectiveMode === "walking" && primaryWaypoints.length === 0
        ? (async () => {
            // Discovery waits for pass 1 — see the discoveryGate comment above.
            await discoveryGate;
            try {
              const { greatWalkChainForCorridor, loadGreatWalks } = await import("@/utils/scenicRoads");
              await loadGreatWalks(); // lazy: walk segments load only now, never on a drive
              const chain = greatWalkChainForCorridor(startCoords, dest.coords, {
                corridorKm: 0.7,
                maxSegments: 6,
                minSegments: 2,
              });
              if (!chain || chain.waypoints.length < 2) return [] as RawRoute[];
              const routes = await fetchBaseRoutesWithFallback({
                startCoords,
                destCoords: dest.coords,
                mode: effectiveMode,
                primaryWaypoints: chain.waypoints,
                detourTolerance: "scenic",
                baseExcludes: [],
                noHwyOverrideSec: Number.POSITIVE_INFINITY,
              });
              tagRoutesSource(routes, "scenic-corridor");
              // Name the streets for the card explainer — but ONLY the curated
              // segments the FINAL snapped route actually travels near. Pushing
              // a waypoint toward a segment doesn't guarantee Mapbox threaded
              // that exact block; it may take a parallel street. Naming a street
              // the route never goes down is just wrong (Morgan: headline said
              // "Through East 10th Street" on a route that never uses it). Keep
              // a segment only when the route passes within ~60 m of its
              // waypoint.
              for (const r of routes) {
                const coords = r.coordinates;
                if (!coords?.length) { r.varietyStreets = []; continue; }
                const kept: string[] = [];
                chain.segments.forEach((s, i) => {
                  const via = chain.waypoints[i];
                  if (via && nearestDistance(via, coords) <= 60) kept.push(s.name);
                });
                r.varietyStreets = kept;
              }
              return routes;
            } catch {
              return [] as RawRoute[];
            }
          })()
        : Promise.resolve([]);

    // WALK THROUGH A PARK (Morgan: "why are we not going through Washington
    // Square Park?"). A great city walk should cross the big green space on the
    // way — Washington Square, the Tuileries, St James's Park. Find the nearest
    // major NAMED park on the corridor and thread the walk through its center so
    // it crosses the park's internal paths. Universal (live OSM, any city), so it
    // also gives the reliable Scenic card where curated street data is thin.
    const walkParkBgPromise: Promise<RawRoute[]> =
      (effectiveMode === "walking" || effectiveMode === "biking") &&
      primaryWaypoints.length === 0
        ? (async () => {
            // Discovery waits for pass 1 — see the discoveryGate comment above.
            await discoveryGate;
            try {
              const { fetchWalkParkVia } = await import("@/api/osm");
              const park = await fetchWalkParkVia(startCoords, dest.coords, { corridorKm: 0.7 });
              if (!park) return [] as RawRoute[];
              const routes = await fetchBaseRoutesWithFallback({
                startCoords,
                destCoords: dest.coords,
                mode: effectiveMode,
                primaryWaypoints: [park.via],
                detourTolerance: "scenic",
                baseExcludes: [],
                noHwyOverrideSec: Number.POSITIVE_INFINITY,
              });
              tagRoutesSource(routes, "scenic-corridor");
              for (const r of routes) r.parkName = park.name;
              return routes;
            } catch {
              return [] as RawRoute[];
            }
          })()
        : Promise.resolve([]);

    // MULTI-STOP PLEASANT (Morgan: "Nice → Umbria → Rome shows only a Fast
    // route"). Every single-corridor pass above is gated to `primaryWaypoints
    // .length === 0`, so a trip WITH stops only ever gets the base Fast route
    // through them. Here we walk each leg (start→stop1, stop1→stop2, …→dest),
    // collect the best curated scenic via(s) per leg, and thread ONE route
    // through [start → leg-1 vias → stop1 → leg-2 vias → … → dest] — honoring
    // every stop in order while making each leg a nicer drive/walk. Tagged
    // scenic-corridor so it competes for the Pleasant/Scenic slot.
    const multiStopPleasantBgPromise: Promise<RawRoute[]> =
      primaryWaypoints.length > 0
        ? (async () => {
            // Discovery waits for pass 1 — see the discoveryGate comment above.
            await discoveryGate;
            try {
              const { multiStopScenicVias, loadGreatWalks } = await import("@/utils/scenicRoads");
              // Walk segments are lazy — load them for a pedestrian multi-stop so
              // scenicRoadsForCorridor(walking) can see them. Driving never loads.
              if (effectiveMode === "walking" || effectiveMode === "biking") await loadGreatWalks();
              const points: LngLat[] = [startCoords, ...primaryWaypoints, dest.coords];
              const vias = multiStopScenicVias(points, effectiveMode, { perLegRoads: 1, maxWaypoints: 1 });
              // LOOP trips (start ≈ end): the whole POINT is the calm way
              // around — never bail to the fast base (Morgan: the loop
              // "taking you on main roads"), and actively exclude
              // motorways so the circle threads back roads by construction.
              const isLoop = haversine(startCoords, dest.coords) <= 500;
              // No vias beyond the stops themselves → nothing nicer to offer than
              // the base Fast route already threads. Bail (the stops are already
              // in the base route) — EXCEPT for loops, which still deserve
              // the no-highway threading of their own waypoints.
              const stopSet = new Set(primaryWaypoints.map((w) => `${w[0]},${w[1]}`));
              const hasScenicVia = vias.some((v) => !stopSet.has(`${v[0]},${v[1]}`));
              if (!hasScenicVia && !isLoop) return [] as RawRoute[];
              const routes = await fetchBaseRoutesWithFallback({
                startCoords,
                destCoords: dest.coords,
                mode: effectiveMode,
                // LOOPS keep THEIR OWN circle (Morgan: "loop does not seem
                // to change with the time") — substituting curated vias
                // here was throwing away the duration-sized waypoints, so
                // every duration collapsed to the same small ring.
                primaryWaypoints: isLoop ? primaryWaypoints : (hasScenicVia ? vias : primaryWaypoints),
                detourTolerance: "scenic",
                baseExcludes: isLoop ? ["motorway"] : [],
                noHwyOverrideSec: Number.POSITIVE_INFINITY,
                // Loops: vias are shaping points, not stops.
                silentVias: isLoop,
              });
              tagRoutesSource(routes, "scenic-corridor");
              return routes;
            } catch {
              return [] as RawRoute[];
            }
          })()
        : Promise.resolve([]);

    // CALM-ROADS GENERATOR (Morgan: "build routes ourselves" / the White Plains
    // "no calmer route" gap). When Mapbox only returns the arterial line and no
    // curated road is near, go FIND the quiet parallel streets ourselves and
    // thread a NEAR-DIRECT route through them. Tagged "calm-corridor" (NOT
    // scenic-corridor) so it competes for the PLEASANT slot, not the long-way
    // Scenic one. Driving, point-to-point, local/regional trips only.
    const CALM_MAX_MILES = 60;
    const calmCorridorBgPromise: Promise<RawRoute[]> =
      effectiveMode === "driving" &&
      primaryWaypoints.length === 0 &&
      // NOTE: curated corridors are NO LONGER excluded. They used to be — the
      // assumption was that the curated-road injection would cover them. But the
      // injection threads the WHOLE curated road (e.g. all of Albany Post Road),
      // producing a +50% "scenic" route, not the NEAR-DIRECT calm hybrid that
      // fits the Pleasant budget. On Briarcliff→Rhinebeck that left Pleasant with
      // no in-budget calm option, so it collapsed to 2 cards. The calm generator
      // builds exactly that hybrid. (This exclusion was reverted once for causing
      // dead-end spurs; calm-corridor routes are now exempt from the dead-end /
      // coherence gate — see isConstructedIntentRoute in scoring.ts — so it's safe.)
      !isCoastalCorridor &&
      tripMilesStraight <= CALM_MAX_MILES
        ? (async () => {
            // Discovery waits for pass 1 — see the discoveryGate comment above.
            await discoveryGate;
            try {
              const { fetchCalmCorridorVias } = await import("@/api/osm");
              const vias = await fetchCalmCorridorVias(startCoords, dest.coords, {
                corridorKm: 1.5,
                max: 4,
              });
              if (vias.length === 0) return [] as RawRoute[];
              try {
                const routes = await fetchBaseRoutesWithFallback({
                  startCoords,
                  destCoords: dest.coords,
                  mode: "driving",
                  primaryWaypoints: vias,
                  detourTolerance: "balanced",
                  baseExcludes: ["motorway"] as const,
                  noHwyOverrideSec: Number.POSITIVE_INFINITY,
                });
                tagRoutesSource(routes, "calm-corridor");
                return routes;
              } catch {
                return [] as RawRoute[];
              }
            } catch {
              return [] as RawRoute[];
            }
          })()
        : Promise.resolve([]);

    // LOOPS HAVE NO HIGHWAY-LEGAL BASE (Morgan: "make it strictly NO
    // highways — zero. google has the no highways option that is better").
    // A loop's base candidate excludes motorways at the request level and
    // gets an infinite no-highway budget — so even when every scenic
    // thread fails, the surviving route CANNOT be a highway loop. Vias
    // stay silent so the circle reads as one drive.
    const isLoopTripGen = haversine(startCoords, dest.coords) <= 500 && primaryWaypoints.length > 0;
    const basePoolPromise: Promise<RawRoute[]> =
      // Reuse the pool we already kicked off above (driving point-to-point) so we
      // don't fire a second identical Mapbox request; otherwise build it now.
      earlyBasePool ??
      fetchBaseRoutesWithFallback({
            startCoords,
            destCoords: dest.coords,
            mode: effectiveMode,
            primaryWaypoints,
            detourTolerance: effectiveTolerance,
            baseExcludes: isLoopTripGen
              ? Array.from(new Set([...baseExcludes, "motorway" as const]))
              : baseExcludes,
            noHwyOverrideSec: isLoopTripGen ? Number.POSITIVE_INFINITY : noHwyOverrideSec,
            silentVias: isLoopTripGen,
          });

    const [poolRoutes, injections] = await Promise.all([
      basePoolPromise,
      Promise.all(injectionFetches),
    ]);
    if (isCancelled()) return;
    const baseRaw: RawRoute[] = [...poolRoutes];
    for (const { src, routes } of injections) {
      tagRoutesSource(routes, src);
      // TRAVERSAL VERIFICATION. A curated-road injection only counts if the
      // resulting route ACTUALLY drives the road for a meaningful length —
      // otherwise the router clipped it, U-turned, or lollipopped it (the
      // "compliance artifact" that produced embarrassing loops). Drop scenic-road
      // candidates that don't traverse a curated road for >= ~0.5 mi so such a
      // route never reaches a card. Other sources pass through unchanged.
      // NO SILENT VERDICTS (rule 17). This gate has been dropping curated roads
      // without a word. Aug 9, current location → Nice:
      //     [scenic-road] injection "Route du Mont Boron": routes=1
      //     [scenic-road] injection "Route de Saint-Jean-Cap-Ferrat": routes=1
      //     [scenic-road] injection "Chemin de Saint-Hospice": routes=1
      //     [scenic] pool=[mapbox-direct:2 mapbox-pleasant:2]     ← none of them
      // Three human-curated roads for the destination, built successfully, gone
      // before classification, and no line naming the killer. That silence is
      // the whole reason the trip has no Scenic card — and rule 16 says
      // provenance decides, so a curated road dying deserves a louder death
      // than an engine candidate's.
      //
      // 800m is also a fixed bar on roads that may themselves be shorter than
      // 800m: Mont Boron and Cap-Ferrat are a couple of km of coastal corniche.
      // The log prints what was actually travelled so we can see whether the
      // router clipped the road or the bar is simply longer than the road.
      const kept =
        src === "scenic-road"
          ? routes.filter((r) => {
              const t = curatedRoadsTravelled(
                { coordinates: r.coordinates, steps: r.steps, distance: r.distance },
                effectiveMode,
                { minMeters: 800 },
              );
              const passed = t.length > 0 && t[0].meters >= 800;
              if (!passed) {
                console.log(
                  `[scenic-road] DROPPED before the pool — traversal check: ` +
                    `${t.length === 0
                      ? "matched NO curated road at all"
                      : `only ${Math.round(t[0].meters)}m on "${t[0].name ?? "?"}"`} ` +
                    `(needs 800m) — route ${Math.round(r.distance / 1000)}km/` +
                    `${Math.round(r.duration / 60)}m`,
                );
              }
              return passed;
            })
          : routes;
      baseRaw.push(...kept);
    }
    perf(`route fetch done (mapbox + injections in parallel, ${baseRaw.length} routes)`);

    // (#175) Tag any not-yet-tagged routes from the fallback path as
    // "mapbox-base". The fetchThreePoolsAndDedup branch already tags
    // its routes individually as mapbox-direct/pleasant/scenic +
    // valhalla-balanced/adventure.
    for (const r of baseRaw) {
      if (getRouteSource(r) === "unknown") tagRouteSource(r, "mapbox-base");
    }
    // Cap geometry for the heavy selection/scoring/overview phase (full geometry
    // is preserved on navCoordinates for navigation). Without this a 35k-point
    // cross-country route blocks the cheap-pass scoring AND the React map render
    // for ~11s each, freezing the app.
    for (const r of baseRaw) { capRouteGeometry(r); r.coordinates = trimOutAndBackSpurs(r.coordinates); }
    // Ring filter on the FIRST-PAINT pool too. Critical ordering: the ratchet
    // protects whatever deliberate route reaches the cards first — so a
    // ballooned coastal thread that slips into the cheap pass would be
    // PROTECTED by the very rule meant to keep good routes. Rings must die
    // before anything renders.
    if (dest?.name !== "Pleasant Loop") {
      for (let i = baseRaw.length - 1; i >= 0; i--) {
        const r = baseRaw[i];
        if (isConstructedRoute(r) && ringDetected(r.coordinates)) {
          perf(`pool: dropped ringed ${getRouteSource(r)} candidate before first paint`);
          baseRaw.splice(i, 1);
        }
      }
    }
    if (isCancelled()) return;

    const avoidHighways = effectiveMode === "driving" &&
      (profile.loves.includes("no-highways") || isScenicTol);
    const routePrefs: Preference[] = isScenicTol && !profile.loves.includes("no-highways")
      ? [...profile.loves, "no-highways"]
      : profile.loves;

    const SAMPLE_CAP: Record<TravelMode, number> = {
      walking: 8, biking: 10, driving: 12,
    };
    const buffer = BUFFER_BY_MODE[effectiveMode];
    let tolerance = TOLERANCE_MULTIPLIER[effectiveTolerance];
    const isScenic = effectiveTolerance === "scenic";
    if (effectiveMode === "walking" && !isScenic) {
      const directKm = haversine(startCoords, dest.coords) / 1000;
      if (directKm < 2) {
        tolerance = Math.min(1.50, Math.max(tolerance, 1.35));
      }
    }
    const retryAttempt = tryAgainOpts?.attempt ?? 0;
    if (retryAttempt > 0) {
      tolerance = Math.max(tolerance, 1.20 + retryAttempt * 0.06);
    }
    if (effectiveMode === "walking" && !isScenic) {
      const directKm = haversine(startCoords, dest.coords) / 1000;
      if (directKm < 2) tolerance = Math.min(tolerance, 1.50);
    }

    // ---------- FAST PATH ----------
    const cheapScore = (rawList: RawRoute[]) => {
      const fastestDur = rawList.length ? Math.min(...rawList.map((r) => r.duration)) : 0;
      return rawList.map((r) => {
        const features = summarizeFeatures(
          r,
          { parks: 0, waterBodies: 0, residentialKm: 0, highwayKm: 0, majorRoadKm: 0, bikeLaneKm: 0, sidewalkKm: 0, footwayKm: 0, noSidewalkKm: 0 },
          { cafes: [], shops: [], landmarks: [] },
          buffer,
        );
        return scoreRoute({
          route: r, features, fastestDuration: fastestDur,
          prefs: routePrefs, community: 50,
          profile: effectiveProfile,
          toleranceMultiplier: tolerance,
          mode: effectiveMode,
        });
      });
    };

    // Pleasant time budget (#91 → #167). Was: max(7 min, 15% of trip).
    // Now: max(15 min, 25% of trip). Morgan: "we want as many beautiful
    // backroads as possible - some of this won't slow down the time too
    // much. for pleasant." The wider budget lets a 90-min highway trip
    // entertain a 110-115 min backroad alternative (Route 9 along the
    // Hudson, for example) instead of capping at 103 min and collapsing
    // the Pleasant slot back onto the Taconic. Scenic still ignores
    // this budget — its own 1.80× multiplier governs.
    // (Morgan, route-philosophy pass) Pleasant may run up to the LARGER of
    // +20 min or 20% of the trip over the fastest route — so a short hop can't
    // balloon, but a long road trip can afford meaningfully more (a 3-hour
    // drive → ~+36 min) for a genuinely nicer way. Scenic gets up to +30% of
    // the fastest time (enforced separately in classifyThreeCards).
    const fastestDurForBudget = baseRaw.length
      ? Math.min(...baseRaw.map((r) => r.duration))
      : 0;
    const pleasantBudgetSec = pleasantBudgetSeconds(fastestDurForBudget);
    // Absolute Pleasant ceiling, computed ONCE from the initial (pre-Valhalla)
    // fastest and passed unchanged to every classification pass. This is the
    // fix for "Pleasant appears then reverts": without it, each pass recomputes
    // the gate off the live pool fastest, so when Valhalla folds in a 78m route
    // the ceiling drops below an already-qualified 95m route and evicts it.
    const pleasantTimeCapSec = (fastestDurForBudget || 0) + pleasantBudgetSec;
    const baseScored = cheapScore(baseRaw);
    // (#129 stage 2) Always return 3 cards — Direct, Pleasant, Scenic —
    // regardless of the user's tolerance preset. The tolerance preset
    // becomes a no-op for selection (UI cleanup follows in stage 3).
    const baseClassified = classifyThreeCards(baseScored, avoidHighways, {
      mode: effectiveMode,
      pleasantBudgetSec,
      pleasantTimeCapSec,
      googleCoords,
      // (#155) Cheap path runs WITHOUT scenic anchors (no Overpass call
      // yet), so Scenic will collapse to Pleasant on overlap. Mark the
      // card as "Searching scenic..." instead of the generic same-as
      // copy — the refresh below will unset this once enrichment loads
      // real anchors.
      scenicAnchorsLoaded: false,
      // PLEASANT LOOP — explicit intent from the destination Index built, not
      // geometry. Stands down the four anti-loop gates a circle would trip.
      isLoop: dest?.name === "Pleasant Loop",
    });
    if (baseScored.length > 0) {
      const cheapFastest = baseScored.reduce((a, b) =>
        a.route.duration <= b.route.duration ? a : b,
      );
      g.setFastestPleasantBaseline(cheapFastest.scores.pleasant);
    }
    g.setScored(baseClassified);
    g.setSelectedClass("pleasant");
    g.setLoading(false);
    // (Morgan) The fast pass (~0.6s) paints a usable set of cards immediately so
    // the user never waits. But the BACKGROUND block below can still add
    // alternatives a few seconds later (the scenic / calm / nicer-towns
    // corridors), so the offering isn't 100% final yet. Mark it PARTIAL — cards
    // are shown (loading is already false) but enrichment is still running, which
    // lets the UI show a quiet "still finding routes" note until we flip to READY
    // at the end. The note matters because a later card (e.g. Scenic) appearing
    // would otherwise read as a glitch.
    g.setStatus("partial", null);
    perf("cheap path = PARTIAL (cards shown; alternatives + descriptions still enriching)");

    // ---------- BACKGROUND: extra alternatives + full enrichment ----------
    // (#177) Architectural cleanup per Morgan: "we don't try to manipulate
    // routes. Routes come from the engines. We choose from them." This
    // block previously injected up to a dozen variations of waypoint-
    // forced fetches — perpendicular nudges, chained anchors, pleasant-
    // waypoint detours, PCH overrides, localBackroadSequence. Every one
    // of them was the engine being TOLD to detour, which is what
    // produced visible loops in the polyline.
    //
    // Now: for driving + no waypoints, fetchThreePoolsAndDedup already
    // gave us Mapbox direct + Mapbox motorway-excluded + Valhalla balanced
    // + Valhalla adventure. That's our 4 different questions to the
    // engines. Walking/biking still get one motorway-excluded supplement
    // for the legacy fallback path. No anchor waypoints. No nudges.
    //
    // "Noticing the lake" — i.e. surfacing routes that happen to pass
    // scenic features — is a SCORING job, not a generation job.
    // summarizeFeatures + scoreRoute measure proximity to parks, water,
    // historic districts, etc., AFTER the engines return their answers.
    // The route that happens to skirt the reservoir wins the Pleasant
    // slot because of its water-proximity score, not because we told the
    // engine to go there.
    const extraRoutes: RawRoute[] = [];
    if (waypoints.length === 0 && avoidHighways && effectiveMode !== "driving") {
      const supplementRoutes = await fetchRoutes(
        startCoords, dest.coords, effectiveMode, [], ["motorway"], noHwyOverrideSec,
      ).catch(() => [] as RawRoute[]);
      tagRoutesSource(supplementRoutes, "mapbox-no-motorway");
      extraRoutes.push(...supplementRoutes);
    }

    if (isCancelled()) return;

    // Fold in the Valhalla back-road alternatives now (they resolved in the
    // background while the Mapbox cards already rendered). On corridors where
    // Mapbox collapsed onto the direct line, this is what finally surfaces a
    // calmer route — a few seconds after first paint, never blocking it.
    const valhallaRoutes = await valhallaBgPromise.catch(() => [] as RawRoute[]);
    if (isCancelled()) return;
    // [raw] PRE-NORMALISATION DUMP (docs/OPEN_ETA_BUG.md, the one-line next
    // step): every route's duration/distance as it stands RIGHT BEFORE the
    // trafficFactor and speed-parity passes below rewrite Valhalla durations.
    // Compare against the [raw] provider lines (fetch time) and the
    // [nav-trace] lines (selection/handover time) to see exactly which stage
    // manufactured the ~44 km/h pricing. Logging only — no behaviour change.
    for (const r of [...baseRaw, ...valhallaRoutes]) {
      // eslint-disable-next-line no-console
      console.log(`[raw] pre-norm ${routeEtaFragment(r, getRouteSource(r))}`);
    }
    // TRAFFIC NORMALIZATION (Morgan's catch). Mapbox routes use the
    // driving-traffic profile, so their durations include live congestion;
    // Valhalla returns FREE-FLOW times (no traffic). Left as-is, a free-flow
    // Valhalla route undercuts the real-traffic Mapbox routes, unfairly steals
    // the "fastest" slot, shrinks the Pleasant budget, and knocks out the
    // pleasant pick a few seconds after first paint — the "routes change when
    // traffic loads" revert. Both engines find ~the same fastest PATH, so the
    // ratio of their fastest durations IS the live traffic factor. Scale every
    // Valhalla time up by it (clamped >=1). This is SYNCHRONOUS on purpose: an
    // earlier version re-timed each Valhalla route with a live Mapbox call here,
    // but those blocking calls added enough latency that the generation got
    // cancelled before the enriched pass ran — and the pleasant route vanished.
    // The displayed ETAs are still refined with real per-route traffic later,
    // off the critical path (useTrafficEtas).
    if (valhallaRoutes.length > 0 && baseRaw.length > 0) {
      const mapboxFastest = Math.min(...baseRaw.map((r) => r.duration));
      const valhallaFastest = Math.min(...valhallaRoutes.map((r) => r.duration));
      const trafficFactor = valhallaFastest > 0 ? Math.max(1, mapboxFastest / valhallaFastest) : 1;
      if (trafficFactor > 1.001) {
        for (const r of valhallaRoutes) r.duration = Math.round(r.duration * trafficFactor);
        perf(`valhalla durations normalized x${trafficFactor.toFixed(3)} for traffic parity`);
      }
      // SPEED PARITY (Morgan, Aug 9, current location → Nice):
      //     Fast & pleasant   7h 08m · 386.3 mi   → 54.2 mph
      //     Scenic route      7h 27m · 471.6 mi   → 65.2 mph
      // 85 extra miles in 19 extra minutes. On the card, side by side, in the
      // user's face. The single ratio above cannot fix this, because the two
      // engines disagree about SPEED, not about a constant: Valhalla's
      // free-flow model is most wrong on exactly the long rural line whose
      // duration matters most, and one scalar taken from the fastest pair
      // can't know that.
      //
      // The invariant, derived only from numbers we already display: NO ROUTE
      // MAY CLAIM A HIGHER AVERAGE SPEED THAN THE FASTEST ROUTE. The fastest
      // route is by definition the most time-efficient line on this trip; a
      // longer alternative that also claims to be quicker per mile is not a
      // measurement, it is a different engine's optimism (rule 20 — a number
      // that couldn't be true isn't data).
      //
      // Only ever LENGTHENS a Valhalla duration, never shortens one, and never
      // touches Mapbox or the fastest route. Worst case it makes an alternative
      // look slightly slower than it drives — which is the honest direction to
      // be wrong in.
      const fastestRef = baseRaw.reduce((a, b) => (a.duration <= b.duration ? a : b));
      const refSpeed = fastestRef.duration > 0 ? fastestRef.distance / fastestRef.duration : 0;
      if (refSpeed > 0) {
        let slowed = 0;
        for (const r of valhallaRoutes) {
          if (!(r.duration > 0) || !(r.distance > 0)) continue;
          const implied = r.distance / r.duration;
          if (implied > refSpeed) {
            r.duration = Math.round(r.distance / refSpeed);
            slowed++;
          }
        }
        if (slowed > 0) {
          perf(
            `valhalla speed parity: ${slowed} route(s) claimed a higher average speed ` +
            `than the fastest line (${(refSpeed * 2.237).toFixed(1)} mph) — durations raised to match`,
          );
        }
      }
    }
    perf(`valhalla folded in (${valhallaRoutes.length} routes)`);

    // Fold in the along-corridor scenic candidate(s) — the "beautiful long way"
    // discovered mid-route (e.g. the Maine coast). Background; never gated paint.
    //
    // HARD DEADLINE (Morgan: "we should not make the finding-routes thing
    // long at all"). On-device profiling showed this discovery phase running
    // 12–26 s when public Overpass mirrors crawl, holding the Pleasant slot
    // hostage the whole time. Each discovery promise now races a shared
    // deadline: whatever has resolved by then folds in, the rest is dropped
    // for THIS search (they keep running underneath and warm the caches, so
    // a re-search of the same trip gets them instantly). The user waits at
    // most DISCOVERY_DEADLINE_MS past the base cards for the upgrade.
    // FORENSIC FIX (Morgan: "this was correct, I shipped it — something
    // broke"). The 12s deadline (July 8-9 speed pass) collided with the
    // same-week Overpass migration (slower corridor queries): discovery
    // started finishing at 13-21s exactly when the deadline dropped it at
    // 12s, so scenic/calm candidates made the pool on SOME runs and not
    // others — the two-card trips, the highway "Scenic", the run-to-run
    // randomness. Fix is NOT more waiting (Morgan: "I don't want that much
    // time") — the deadline stays at 12s, but stragglers now FOLD IN LATE
    // (see the late-fold pass below) instead of being dropped.
    // De-dupe by approximate duration+distance signature.
    const sig = (r: RawRoute) =>
      `${Math.round(r.duration / 60)}|${Math.round(r.distance / 100)}`;
    const seenSig = new Set<string>();
    const raw: RawRoute[] = [];
    // Order matters: the discovered scenic-corridor routes come BEFORE Valhalla,
    // so if a back-road shares their duration signature, the (tagged) coastal
    // route wins the slot and survives to the classifier — otherwise it was
    // silently deduped away and the Scenic card could never form.
    // Discovered-corridor routes (scenic-corridor, calm-corridor) are kept even
    // when their duration+distance signature collides with an earlier route.
    // Their whole purpose is distinct geometry (a calmer / prettier line that
    // can run at nearly the same time as Direct), and a coarse sig collision
    // would silently delete them before the classifier — which does proper
    // geometry-based dedup — ever sees them.
    const isDiscoveredCorridor = (r: RawRoute) => {
      const s = getRouteSource(r);
      // charming-towns is a discovered corridor too — it must survive dedup
      // AND be allowed to claim Scenic in the late-fold ratchet.
      return s === "scenic-corridor" || s === "calm-corridor" || s === "charming-towns";
    };
    // RETRACE FILTER (Morgan's rule, refined same-day): "loopy is ok — we
    // don't want to double back on the same road." The sin is not a looping
    // SHAPE (a coastal swing through Mystic/Newport is the product; peninsulas
    // are near-returns by nature) — it's riding the same pavement twice
    // (Hackley repro: out, corkscrew a neighborhood, back down the SAME road).
    // So: judge candidates by selfOverlapFraction (how much of the route lies
    // on top of itself), not by shape heuristics. The first cut here used
    // isCleanAnchorRoute's near-return/backtrack detectors and promptly ate
    // the 427-minute charming-towns Scenic — shape heuristics can't tell a
    // grand tour from a lollipop; self-overlap can.
    const isWaypointDerived = (r: RawRoute) => {
      const s = getRouteSource(r);
      return s === "scenic-corridor" || s === "calm-corridor" || s === "valhalla-adventure" || s === "scenic-anchor";
    };
    // GOOGLE IS A BACKUP NOW (Morgan, Aug 9: "drop google to backup — no reason
    // to pay hundreds of dollars for nothing"). It joins the pool ONLY when our
    // own engine returned no route at all.
    //
    // This REVERSES the July 17 approval preserved below. That decision rested
    // on the Fast card showing Google's line to the user; #24 then removed every
    // Google mention from user-facing copy (rule 4), and nobody revisited the
    // premise. What was left was a candidate that:
    //   - costs ~$150–250/mo projected at 10k users,
    //   - 502s in bursts (googleDirections.ts:78, and 3 more on Aug 9),
    //   - ships roadClassMeters:{} and steps[].classes:[] — no road-class data
    //     at all, so it reads as 0% highway and looked like the calmest route
    //     in the pool for three weeks,
    //   - and delivered a benefit no user could see, because we may not name it.
    // Mapbox already runs the `driving-traffic` profile (routing.ts:112), so
    // live-congestion ETAs — Google's one real advantage — were never exclusive.
    //
    // The July 17 reasoning, kept so this isn't re-litigated from scratch:
    //   "The Fast card shows Google's line — but it was DISPLAY-ONLY, never a
    //   candidate, so the rules could never crown it. Home Depot repro: the
    //   natural riverside route (Google's actual fastest) existed on screen and
    //   'wasn't even an option' for Pleasant. Now it competes like everything
    //   else." That holds ONLY while the user can see whose route it is.
    const googleAsCandidate: RawRoute[] = [];
    const engineHasADirectRoute = baseRaw.length > 0;
    if (!engineHasADirectRoute &&
        args.googleRoute?.coordinates && args.googleRoute.coordinates.length >= 2 &&
        args.googleRoute.durationS > 0) {
      const gr: RawRoute = {
        coordinates: args.googleRoute.coordinates.slice(),
        distance: args.googleRoute.distanceM ?? 0,
        duration: args.googleRoute.durationS,
        // Google gives no turn steps. One synthetic full-length step keeps
        // every steps-consumer safe (color notes, curated-road matching,
        // trip details). If this route WINS a card and the user taps Start,
        // NavigationView's off-route recalc rebuilds real turn-by-turn from
        // the first GPS fix — interim until we fetch proper steps at Start.
        steps: [{
          instruction: "Follow the route",
          distance: args.googleRoute.distanceM ?? 0,
          duration: args.googleRoute.durationS,
          roadName: "",
          classes: [],
        } as RawRoute["steps"][number]],
        motorwayMeters: 0,
        highTrafficMeters: 0,
        tunnelMeters: 0,
        tollMeters: 0,
        epicHighwayMeters: 0,
        epicHighwayLabels: [],
        roadClassMeters: {},
      };
      tagRouteSource(gr, "google-direct");
      googleAsCandidate.push(gr);
      // NO SILENT VERDICTS (rule 17). If this line ever appears in a normal run
      // it means our own engine returned nothing — that is the story, not Google.
      perf("google route joined the pool as BACKUP — engine returned no direct route");
    } else if (args.googleRoute?.coordinates && args.googleRoute.coordinates.length >= 2) {
      perf(`google route held back (backup only) — engine returned ${baseRaw.length} route(s)`);
    }
    /** Admit candidates to the shared pool: geometry cap, spur trim, retrace
     *  filter, ring filter, signature dedup. Extracted from the single inline
     *  loop (Morgan, Aug 23) so it can run TWICE — once on the core router
     *  pool before discovery blocks for up to 12s, once on whatever discovery
     *  adds — while `raw` and `seenSig` stay shared, so "admitted" means
     *  exactly what it meant when there was one pass.
     *
     *  ORDER NOTE. The single loop ran baseRaw, google, extraRoutes,
     *  scenicCorridor, valhalla, and the comment below explains why the
     *  discovered corridors preceded Valhalla. Split into two passes, Valhalla
     *  now goes first. That precedence is preserved anyway: scenic-corridor,
     *  calm-corridor and charming-towns are all sig-EXEMPT (isDiscoveredCorridor),
     *  and the only non-exempt discovery sources — walk/bike adventure and
     *  multi-stop — exist only in walking/biking mode, where valhallaBgPromise
     *  resolves to [] (it is gated to driving). The two sets never meet. */
    const admitToPool = (candidates: RawRoute[]): RawRoute[] => {
      const admitted: RawRoute[] = [];
      for (const r of candidates) {
        // baseRaw is already capped above; this covers Valhalla / scenic / calm
        // routes (idempotent — a no-op for anything already short or capped).
        capRouteGeometry(r);
        // Snip little out-and-back retrace spurs (Morgan: route goes down a block
        // and turns right back) left by threading a waypoint just off the line.
        // Heading-guarded so real turns/detours are untouched; keeps the great
        // roads, drops only the dead retrace.
        r.coordinates = trimOutAndBackSpurs(r.coordinates);
        // (Morgan, La Crèche July 15 — both directions of the same bug.) The old
        // fraction-based check (a) flagged the WINDING Rockefeller park road as a
        // retrace because its own curves pass within 25m of each other — the best
        // candidate in the pool was dropped clean — and (b) never examined
        // engine-built routes at all, so a scenic route that visibly drove out
        // and back on the same stalk sailed through. The honest measure: how many
        // CONTIGUOUS meters does the route re-drive the same road (direction-
        // aware — curves and perpendicular crossings don't count). Applied to
        // EVERY candidate, any source.
        {
          // Coastal threads get a looser leash (Morgan, Aug 2 — the bounce,
          // caught in the log frame by frame: coastal-pch on the Scenic card at
          // 1.5s, then REMOVED FROM THE POOL by this very check during the 15s
          // rebuild, after which an inland lookalike inherited the slot). A
          // route deliberately threaded through a dozen shoreline vias re-drives
          // a few hundred meters at every town it was told to touch — that's
          // the hug working, not a navigation error. 2.5km still catches a
          // genuinely broken out-and-back (the M-116 spur alone cost ~5km).
          // CURATED ROADS GET THE COASTAL LEASH TOO (Morgan, Aug 9 — Back Bay →
          // New City). The log named its own victim:
          //
          //   pool: dropped doubling-back scenic-road candidate (3419m re-driven)
          //
          // 3.4 km of re-driving across a two-hundred-mile drive — under 2% — and
          // it cost the trip its Scenic card. coastal-pch already has the 2.5 km
          // leash for exactly this reason ("a route deliberately threaded through
          // a dozen shoreline vias re-drives a few hundred meters at every town it
          // was told to touch"), and scenic-road is threaded the same way, through
          // the road's own curated samples. Rule 16: the filter must know WHO
          // BUILT the route. It was written for machine-picked waypoints, whose
          // doubling-back is our fault; a road a person chose gets the same trust
          // the coast does. Anonymous engine candidates keep the 500 m bar.
          // ...and the leash SCALES, because 2,500 m is another absolute number
          // over an unbounded trip. Back Bay -> New City re-drove 3,419 m, which
          // clears the flat coastal leash and would still have been dropped: on a
          // 200-mile drive that is barely 1%. Same defect class as the five gates
          // fixed on Aug 8 — right in the Hudson Valley, nonsense at road-trip
          // scale. Floor stays 2,500 m so short coastal threads behave exactly as
          // before (Michigan's M-116 spur cost ~5 km and is still caught).
          const retraceM = contiguousRetraceMeters(r.coordinates);
          const curatedSrc = getRouteSource(r);
          const isCuratedThread = curatedSrc === "coastal-pch" || curatedSrc === "scenic-road";
          const retraceLimit = isCuratedThread
            ? Math.max(2500, (r.distance ?? 0) * 0.02)
            : 500;
          if (retraceM >= retraceLimit) {
            perf(
              `pool: dropped doubling-back ${getRouteSource(r)} candidate (${Math.round(retraceM)}m re-driven)`,
            );
            continue;
          }
        }
        // Ring filter — see ringDetected. Constructed candidates only;
        // whole-trip loops are rings by design.
        if (dest?.name !== "Pleasant Loop" && isConstructedRoute(r) && ringDetected(r.coordinates)) {
          perf(`pool: dropped ringed ${getRouteSource(r)} candidate (returns beside its own line after 12km+)`);
          continue;
        }
        const k = sig(r);
        if (seenSig.has(k) && !isDiscoveredCorridor(r)) continue;
        seenSig.add(k);
        raw.push(r);
        admitted.push(r);
      }
      return admitted;
    };

    // ===================================================================
    //  ENRICH BEFORE DISCOVER (Morgan, Aug 23 — architecture approved)
    // ===================================================================
    // "I want to change the routing sequence so trustworthy cards are produced
    //  before expensive discovery work... The first cards we consider
    //  authoritative should be based on real enrichment, not cheap placeholder
    //  scores."
    //
    // What this replaces, measured on Boston → Portsmouth:
    //   794ms   base routes
    //   1639ms  valhalla folded in
    //   8082ms  scenic corridor discovery returns   (+6,443ms of blocking)
    //   9654ms  enrichment START
    //   25111ms READY at the ceiling — enrichment abandoned, cards left CHEAP
    // Discovery spent two thirds of the budget before enrichment issued its
    // first request, and what the user kept was placeholder scoring.
    //
    // Now: the core router pool (Mapbox + Valhalla, Google only as backup) is
    // already complete at this point in the file. Enrich and classify THAT
    // first, emit it as authoritative, and only then wait on discovery.
    //
    // THE TIMING TRICK, stated so nobody "tidies" it away: `discoveryDeadline`
    // is armed BELOW but BEFORE the pass-1 await, so its 12s clock runs
    // CONCURRENTLY with pass-1 enrichment instead of serially ahead of it. The
    // discovery promises themselves were kicked off ~2000 lines above. That is
    // where the wall-clock saving comes from — not from waiting less.
    const pipe = (label: string) =>
      // eslint-disable-next-line no-console
      console.log(`[pipeline] ${label} @${Math.round(performance.now() - T0)}ms`);

    const corePool = admitToPool([...baseRaw, ...googleAsCandidate, ...valhallaRoutes]);
    // Corridor length for telemetry: the SHORTEST candidate, i.e. roughly the
    // direct route. It is the length enrichment has to cover, which is what
    // drives the cell count, which is what drives whether the ceiling is met.
    // Stamped once here so every terminal point below has it.
    if (corePool.length > 0) {
      const shortest = Math.min(...corePool.map((r) => r.distance).filter((d) => Number.isFinite(d)));
      if (Number.isFinite(shortest)) _telemetryRouteM = shortest;
    }
    {
      const counts: Record<string, number> = {};
      for (const r of corePool) {
        const s = getRouteSource(r) ?? "engine";
        counts[s] = (counts[s] ?? 0) + 1;
      }
      pipe(
        `CORE ROUTES ${corePool.length} — ` +
        (Object.entries(counts).map(([s, n]) => `${s}×${n}`).join(" ") || "none"),
      );
    }

    // ---- enrichment configuration (shared by both passes) ----
    const enrichLimit = effectiveMode === "walking" ? 8 : 3;
    // (Morgan) CROSS-COUNTRY FREEZE GUARD. Per-route enrichment tiles the route
    // at 12 km and runs POI searches over the route's bbox. On a transcontinental
    // trip (NY → LA, ~2,800 mi, ~35k coordinates) that's ~125 Overpass tiles plus
    // a US-sized POI bbox PER enriched route — the queries never return and the
    // cards spin forever ("Checking back roads…" / "Comparing with the direct
    // route…" hang). Beyond the scenic-discovery range there's no pleasant detour
    // to find anyway (calm/scenic corridor generation is already gated off), so
    // skip the heavy per-route OSM/POI enrichment entirely and let the trip settle
    // to the fast route fast. Routes still score on Mapbox-derived features via the
    // cheap path inside enrichPool.
    const MAX_ENRICH_MILES = MAX_SCENIC_CORRIDOR_MILES; // 400 — same range as discovery
    const enrichmentTooFar = tripMilesStraight > MAX_ENRICH_MILES;
    // CRITICAL (data-confirmed): also score the calm / pleasant-intent
    // candidates, not just the fastest. Enrichment is what gives a route a REAL
    // pleasantness score; without it a route keeps the flat cheap baseline
    // (~23) and can NEVER clear the Pleasant quality bar. The genuine Pleasant
    // route is slower than Direct by design, so the fastest-N cut was shutting
    // it out of scoring entirely — Pleasant collapsed on every trip. Add the
    // calmer discovered/alternative routes within a generous (+40 min) window.
    const PLEASANT_INTENT_SOURCES = new Set([
      "mapbox-pleasant", "calm-corridor", "valhalla-balanced", "scenic-corridor",
    ]);
    // COST GUARD (Google Places $$). OSM enrichment is FREE and runs on every
    // top route, but the paid Google Places "along route" search is not: it
    // fires up to `maxSamples` Nearby Search (New) calls PER route. Fanning it
    // across all enriched candidates × 30 samples each is what ran up 70k+
    // Places calls in testing AND tripped Google's per-minute rate limit. So we
    // cap the PAID call to a small set — the routes that realistically become
    // cards. Pleasant-intent routes go in first (they become the Pleasant card
    // and need the café/attraction flavor most), then we fill from the fastest.
    const PLACES_ENRICH_CAP = effectiveMode === "walking" ? 5 : 4;
    // SHARED across both passes on purpose. Pass 2 spends what pass 1 left
    // rather than opening a fresh allowance, so the reorder cannot quietly
    // double the number of enrichment fan-outs or paid Places calls.
    const deepEnrich = new Set<RawRoute>();
    const placesEnrich = new Set<RawRoute>();
    /** Same selection rules as the old single pass — fastest-N, then
     *  pleasant-intent within +40 min, then the Places cap — applied against
     *  the shared sets.
     *
     *  ONE DELIBERATE CONSEQUENCE, said plainly (rule 17): the fastest-N slots
     *  are claimed by the CORE pool, because it goes first. A discovered route
     *  that turns out to be faster than a core route can no longer displace one
     *  of those three. It can still be enriched through the pleasant-intent
     *  headroom (scenic-corridor and calm-corridor are both in
     *  PLEASANT_INTENT_SOURCES, and that loop runs to enrichLimit + 4), which
     *  is the case that actually matters — Pleasant and Scenic. */
    const chooseEnrichTargets = (pool: RawRoute[], fastest: number) => {
      if (enrichmentTooFar) return;
      const ranked = [...pool].sort((a, b) => a.duration - b.duration);
      for (const r of ranked) {
        if (deepEnrich.size >= enrichLimit) break;
        deepEnrich.add(r);
      }
      const calmBudgetSec = fastest + 40 * 60;
      for (const r of ranked) {
        if (deepEnrich.size >= enrichLimit + 4) break;
        if (PLEASANT_INTENT_SOURCES.has(getRouteSource(r)) && r.duration <= calmBudgetSec) {
          deepEnrich.add(r);
        }
      }
      for (const r of deepEnrich) {
        if (placesEnrich.size >= PLACES_ENRICH_CAP) break;
        if (PLEASANT_INTENT_SOURCES.has(getRouteSource(r))) placesEnrich.add(r);
      }
      for (const r of deepEnrich) {
        if (placesEnrich.size >= PLACES_ENRICH_CAP) break;
        placesEnrich.add(r);
      }
    };

    // (#171) ONE corridor-wide OSM environment so classifyThreeCards has a
    // complete dead-end / driveway dataset for the area. Fetched for the CORE
    // routes now; §pass 2 tops it up for discovered geography only (Morgan,
    // decision 2 — "Do not blindly redo the full corridor OSM fetch").
    //
    // The corridor OSM feeds dead-end detection only — a refinement, not a
    // gate. Overpass is a free public API and a cold query can take 20-30s,
    // which is dead time the user spends staring at a half-loaded result.
    // Bound it: if it hasn't returned in CORRIDOR_OSM_BUDGET_MS, proceed with
    // empty environment (dead-end detection degrades gracefully). The real
    // fetch keeps running in the background and writeCache()s its tiles, so a
    // repeat of the same trip resolves instantly.
    //
    // NOT AWAITED HERE (Aug 20, measured). Its only consumers are the
    // classification passes below. Held as a shared promise and awaited at the
    // point of use, so total wait becomes max(corridor, enrichment) instead of
    // corridor + enrichment.
    const CORRIDOR_OSM_BUDGET_MS = 8000;
    const emptyEnv: OsmEnvironment = { points: [], lines: [] };
    const coreCorridorCoords = corePool.map((r) => r.coordinates);
    const coreCorridorOsmEnvPromise: Promise<OsmEnvironment> = enrichmentTooFar
      ? Promise.resolve(emptyEnv)
      : Promise.race([
          fetchOsmEnvironmentForRoutes(coreCorridorCoords).catch(() => emptyEnv),
          new Promise<OsmEnvironment>((res) => setTimeout(() => res(emptyEnv), CORRIDOR_OSM_BUDGET_MS)),
        ]);
    perf("corridor OSM kicked off for the CORE pool (runs alongside pass-1 enrichment)");

    // Parks the route runs THROUGH (point-in-area). ONE Overpass `is_in` call
    // for the corridor, fired as a shared PROMISE so it runs CONCURRENTLY with
    // per-route enrichment rather than adding latency — and raced against a
    // budget so a slow/cold Overpass can't stall the result. Walk/bike only.
    const enclosingParksPromise: Promise<string[]> =
      enrichmentTooFar || effectiveMode === "driving"
        ? Promise.resolve([])
        : Promise.race([
            fetchThroughParkNames(corePool[0]?.coordinates ?? []).catch(() => [] as string[]),
            new Promise<string[]>((res) => setTimeout(() => res([]), 6_000)),
          ]);

    // ---- lifecycle flags (Morgan, decision 1) ----
    // `authoritativeCardsEmitted` — pass 1 enriched and classified the core
    //   routes. From this instant nothing may put CHEAP scoring back on screen.
    //   This is the flag that gates.
    // `discoveryComplete`  — the pass-2 classification has been emitted (or
    //   consciously skipped). Before it, a late-arriving discovery route can
    //   still be properly enriched by pass 2; after it, it can only be added,
    //   never enriched, so the late fold is the last word.
    let authoritativeCardsEmitted = false;
    let discoveryComplete = false;
    let authoritativeClassified: ClassifiedRoute[] | null = null;

    // THE AUTHORITATIVE CLASSIFICATION'S AVOID-HIGHWAY FLAG. Hoisted from just
    // above the old final classify, because pass 1 is now an authoritative
    // classification too and the two must ask the same question.
    //
    // This is NOT the same flag as `avoidHighways` a few hundred lines up:
    // that one is preference-driven (no-highways loved, or scenic tolerance)
    // and it governs the CHEAP passes. The enriched classification has always
    // used "driving means consider avoiding highways", and pass 1 inheriting
    // the cheap flag instead would mean the cards changed meaning between
    // pass 1 and pass 2 on a default-tolerance driving trip — a reshuffle with
    // no cause the user could see.
    const avoidHighwayRoutes = effectiveMode === "driving";

    // LATE FOLD (Morgan): discovery results that missed the 12s deadline are
    // no longer dropped — when the coastal/charming-towns/calm candidates
    // finally arrive (13-21s under current Overpass latency), they join the
    // pool and the cards upgrade in place. Never blocks anything. Once the
    // FINAL enriched cards have rendered, the fold turns ADDITIVE-ONLY:
    // it may fill a slot the final classification left empty ("keep the
    // scenic route and ADD a pleasant route") but never replaces or
    // rearranges cards the user is already looking at ("wait what just
    // happened").
    //
    // REGISTERED BEFORE PASS 1 (Aug 23 reorder). It used to sit after the pool
    // build, which under the new order would be after the authoritative cards
    // and after the ceiling could already have fired — and the ceiling path
    // returns. Registering it here keeps the existing safety net intact: a
    // straggler discovery route can still ADD a card on a search whose
    // enrichment ran out of budget.
    void Promise.allSettled([
      scenicCorridorBgPromise,
      calmCorridorBgPromise,
      townsCorridorBgPromise,
      valhallaBgPromise,
    ]).then((settled) => {
      if (isCancelled()) return;
      const fresh: RawRoute[] = [];
      for (const s of settled) {
        if (s.status !== "fulfilled") continue;
        for (const r of s.value as RawRoute[]) {
          capRouteGeometry(r);
          r.coordinates = trimOutAndBackSpurs(r.coordinates);
          // Same direction-aware rule as the main pool (see comment there).
          if (contiguousRetraceMeters(r.coordinates) >= 500) continue;
          // Same ring rule as the main pool.
          if (dest?.name !== "Pleasant Loop" && isConstructedRoute(r) && ringDetected(r.coordinates)) {
            perf(`pool: dropped ringed ${getRouteSource(r)} late candidate`);
            continue;
          }
          // IDENTITY GUARD (added with the Aug 23 reorder). Discovered
          // corridors are sig-EXEMPT, so the sig check above cannot stop the
          // SAME route object being admitted twice — once by the main
          // admission, once here — and this fold now runs earlier, so the
          // overlap window is wider. Object identity settles it.
          if (raw.includes(r) || fresh.includes(r)) continue;
          const k = sig(r);
          if (seenSig.has(k) && !isDiscoveredCorridor(r)) continue;
          seenSig.add(k);
          fresh.push(r);
        }
      }
      if (fresh.length === 0) return;
      raw.push(...fresh);
      const upgraded = classifyThreeCards(cheapScore(raw), avoidHighways, {
        mode: effectiveMode,
        pleasantBudgetSec,
        pleasantTimeCapSec,
        googleCoords,
        scenicAnchorsLoaded: true,
        isLoop: dest?.name === "Pleasant Loop",
      });
      if (isCancelled()) return;
      // THE GATE IS `authoritativeCardsEmitted`, NOT `discoveryComplete`
      // (Morgan, decision 1: "those cards are authoritative and must never
      // regress to cheap scoring"). `upgraded` above is a CHEAP classification.
      // The moment pass 1 has put enriched cards on screen, this fold may only
      // add to them or hand Scenic to a discovered corridor — it may never
      // replace them wholesale. Before pass 1 emits, the old full-replace
      // behaviour is unchanged.
      //
      // `discoveryComplete` is logged rather than gated on, and the difference
      // is real: while it is false the routes this fold just pushed into `raw`
      // can still be picked up and properly ENRICHED by pass 2; once it is
      // true, this fold's cheap classification is the last word they will get.
      if (authoritativeCardsEmitted && authoritativeClassified) {
        if (!discoveryComplete) {
          perf(
            `late fold: ${fresh.length} route(s) arrived before pass 2 — they joined ` +
            `the pool and may still be enriched`,
          );
        }
        // RATCHET (Morgan, Hackley water-route repro): cards may only improve.
        // Two moves are allowed after the final cards render:
        //  1. Fill a slot the final classification left empty ("keep the
        //     scenic route and ADD a pleasant route").
        //  2. A DISCOVERED corridor route (built along water/parks — the
        //     whole point of the app) takes the Scenic slot from a
        //     non-discovered filler. "Scenic-corridor ALWAYS takes Scenic"
        //     must survive the deadline too — otherwise the river route
        //     arrives 3s late and loses to "a mix of local and main roads".
        // Nothing is ever removed or downgraded ("wait what just happened").
        let merged = authoritativeClassified;
        const uScenic = upgraded.find((c) => c.classification === "scenic");
        const fScenic = merged.find((c) => c.classification === "scenic");
        if (
          uScenic &&
          fScenic &&
          uScenic.route !== fScenic.route &&
          isDiscoveredCorridor(uScenic.route) &&
          !isDiscoveredCorridor(fScenic.route)
        ) {
          merged = merged.map((c) => (c === fScenic ? uScenic : c));
          perf(
            `late fold: discovered ${getRouteSource(uScenic.route)} route took Scenic from ${getRouteSource(fScenic.route)}`,
          );
        }
        // A PLACEHOLDER IS AN EMPTY SLOT (Morgan, Aug 1 — Toronto).
        //
        // classifyThreeCards emits fallbackCard() stand-ins when a slot has no
        // real candidate, flagged `sameAsClass`. Index strips them before
        // render, which is why the console reads "reclassified cards: fastest,
        // pleasant, scenic" and then shows one card.
        //
        // But this ratchet counted them as FILLED. On a long trip the genuine
        // discovery route — the charming-towns / scenic corridor — always
        // arrives after the deadline, and it was rejected here as a duplicate
        // of a slot that was never really occupied. Bridgeport → Toronto lost
        // its Stockbridge scenic (11h46m against a 7h21m fast) exactly this
        // way; the shipped 14 July build has no ratchet at all and simply let
        // the late candidate through.
        //
        // The ratchet's own promise is "fill a slot the final classification
        // left empty". A placeholder is empty. It just doesn't look it.
        const have = new Set(
          merged.filter((c) => !c.sameAsClass).map((c) => c.classification),
        );
        const additions = upgraded.filter(
          (c) =>
            !have.has(c.classification) &&
            !merged.some((f) => f.route === c.route),
        );
        if (merged !== authoritativeClassified || additions.length > 0) {
          // Drop the placeholder a real route is replacing, so the slot holds
          // one card rather than a stand-in plus its replacement.
          const addedClasses = new Set(additions.map((c) => c.classification));
          const withoutStandIns = merged.filter(
            (c) => !(c.sameAsClass && addedClasses.has(c.classification)),
          );
          authoritativeClassified = [...withoutStandIns, ...additions];
          g.setScored((prev) => neverShrink(prev, authoritativeClassified!));
          if (additions.length > 0)
            perf(
              `late fold: added ${additions.map((a) => a.classification).join(",")} card(s) after final classify`,
            );
        }
        return;
      }
      // Pre-final pass: the ratchet above guards only the post-final window,
      // so this path is where two cards could become one. See neverShrink.
      g.setScored((prev) => neverShrink(prev, upgraded));
      perf(`late fold: +${fresh.length} discovery routes joined after deadline`);
    });

    const DISCOVERY_DEADLINE_MS = 12_000;
    // The 12s clock starts when discovery is RELEASED, not when it is armed —
    // otherwise the whole window would elapse during pass-1 enrichment and
    // discovery would be dropped before it ever issued a request.
    const discoveryDeadline = new Promise<RawRoute[]>((res) => {
      void discoveryGate.then(() =>
        setTimeout(() => res([] as RawRoute[]), DISCOVERY_DEADLINE_MS),
      );
    });
    const bounded = (p: Promise<RawRoute[]>) =>
      Promise.race([p.catch(() => [] as RawRoute[]), discoveryDeadline]);
    // ARMED, NOT AWAITED. The 12s deadline is now ticking while pass 1 enriches
    // below. This is the whole point of the reorder — see the header comment.
    const discoveryAllPromise = Promise.all([
      bounded(scenicCorridorBgPromise),
      bounded(walkAdventureBgPromise),
      bounded(walkVarietyBgPromise),
      bounded(walkParkBgPromise),
      bounded(multiStopPleasantBgPromise),
      bounded(calmCorridorBgPromise),
      bounded(townsCorridorBgPromise),
    ]);

    // ======================= PASS 1 — CORE POOL =======================
    const fastestDurCore = corePool.length
      ? Math.min(...corePool.map((r) => r.duration))
      : 0;
    chooseEnrichTargets(corePool, fastestDurCore);
    // The same budget formula as the single-pass version, computed at the same
    // point relative to the ceiling — except enrichment now reaches it FIRST,
    // which is the entire change. The 4s floor is kept: this is the pass whose
    // failure means cheap cards, so it always gets a real attempt.
    const pass1BudgetMs = Math.max(
      4_000,
      READY_CEILING_MS - (performance.now() - T0),
    );
    pipe(
      `PASS-1 ENRICHMENT start — pool=${corePool.length} deep=${deepEnrich.size} ` +
      `places=${placesEnrich.size} budget=${Math.round(pass1BudgetMs)}ms` +
      (enrichmentTooFar ? ` (SKIPPED: trip ${Math.round(tripMilesStraight)}mi > ${MAX_ENRICH_MILES}mi)` : ""),
    );
    const pass1 = corePool.length === 0
      ? null
      : await enrichPool(corePool, {
          fastestDuration: fastestDurCore,
          deepEnrich, placesEnrich,
          routePrefs,
          profile: effectiveProfile,
          prioritizeCommunity,
          toleranceMultiplier: tolerance,
          mode: effectiveMode,
          buffer,
          sampleCap: SAMPLE_CAP[effectiveMode],
          enclosingParksPromise,
          budgetMs: pass1BudgetMs,
          label: "pass1",
          isCancelled,
        });
    if (isCancelled()) return;

    let pass1Entries: EnrichedRoute[] = [];
    if (pass1) {
      pipe(
        `PASS-1 ENRICHMENT ${pass1.ok ? "OK" : "TIMED OUT"} in ${pass1.ms}ms — ` +
        `deep ${pass1.deepSettled}/${pass1.deepAttempted} settled`,
      );
    }
    if (pass1 && pass1.ok) {
      pass1Entries = pass1.routes;
      // The authoritative classification. Same options as the old final
      // classify, with one honest difference: scenicAnchorsLoaded is FALSE
      // because discovery has not landed yet, so the Scenic card correctly
      // says "Searching scenic…" rather than claiming there is nothing there.
      const classifiedCore = classifyThreeCards(
        pass1Entries.map((e) => e.scored),
        avoidHighwayRoutes,
        {
          excludeCoords: shownPleasantRef.current,
          skipRank: tryAgainOpts?.attempt ?? 0,
          mode: effectiveMode,
          isLoop: dest?.name === "Pleasant Loop",
          pleasantBudgetSec,
          pleasantTimeCapSec,
          googleCoords,
          scenicAnchorsLoaded: false,
          // Bounded at 8s from its own start, well before this await.
          osmEnv: await coreCorridorOsmEnvPromise,
        },
      );
      if (isCancelled()) return;
      const fastestCore = pass1Entries.reduce((a, b) =>
        a.scored.route.duration <= b.scored.route.duration ? a : b,
      );
      g.setFastestPleasantBaseline(fastestCore.scored.scores.pleasant);
      authoritativeCardsEmitted = true;
      g.setScored((prev) => {
        const guarded = neverShrink(prev, classifiedCore);
        authoritativeClassified = guarded;
        return guarded;
      });
      authoritativeClassified ??= classifiedCore;
      // THE PROOF LINE (Morgan: "I want to be able to prove on a real long
      // route that the cards at the 25-second ceiling are enriched cards").
      // Every card names whether ITS OWN route went through deep enrichment.
      {
        const deepRoutes = new Set(pass1Entries.filter((e) => e.deep).map((e) => e.route));
        pipe(
          "AUTHORITATIVE CARDS — " +
          (authoritativeClassified ?? []).map((c) =>
            `${c.classification}=${getRouteSource(c.route) ?? "engine"}/` +
            `${Math.round(c.route.duration / 60)}m/enriched=${deepRoutes.has(c.route) ? "yes" : "NO"}`,
          ).join(" "),
        );
      }
      // Status stays PARTIAL: real cards are on screen, discovery is still out.
      g.setStatus("partial", null);
      // Pass 1 became authoritative — enriched cards, inside budget.
      _telemetryVerdict = "enriched";
      _telemetryBudgetMs = pass1BudgetMs;
    } else if (pass1) {
      // HARD READY CEILING (Morgan: "we can't wait 45 seconds"). Pass 1 could
      // not finish, so nothing enriched exists to show and there is no budget
      // left to wait for discovery either. The cheap cards from first paint
      // stay and the clock stops — a right answer at 2s beats a blind reshuffle
      // at 45s. The late fold is already registered above, so a straggler
      // discovery route can still ADD a card after this returns.
      discoveryComplete = true;
      g.setStatus("ready", null);
      // THE case this instrumentation exists for: the ready ceiling was missed
      // and the cards are cheap-scored. `overshoot_ms` on the row is
      // elapsed - budget, which is the distribution Morgan asked for.
      _telemetryVerdict = "degraded";
      _telemetryBudgetMs = pass1BudgetMs;
      pipe("PASS-1 NOT AUTHORITATIVE — cards on screen are still cheap-scored");
      // DEGRADED RESULT, SAID OUT LOUD (Morgan, Aug 23 — safety net A). Its job
      // is to make the degradation COUNTABLE (Sentry / console) rather than
      // something we extrapolate from a handful of hand-run searches.
      // eslint-disable-next-line no-console
      console.warn(
        `[degraded] Pleasant/Scenic cards are from CHEAP scoring — pass-1 ` +
        `enrichment never became authoritative for this search. ` +
        `corridor=${dest?.name ?? "trip"} mode=${effectiveMode} ` +
        `at=${startCoords[1].toFixed(3)},${startCoords[0].toFixed(3)} ` +
        `core=${corePool.length} deep=${pass1.deepSettled}/${pass1.deepAttempted} ` +
        `budgetMs=${Math.round(pass1BudgetMs)} ` +
        `elapsedMs=${Math.round(performance.now() - T0)}`,
      );
      return;
    }

    // ======================= DISCOVERY (starts HERE, not earlier) ==========
    // Pass 1 is done with the box. Let discovery have it.
    pipe("DISCOVERY RELEASED — pass 1 finished; discovery may now use Overpass");
    releaseDiscovery();
    const discoveryT0 = performance.now();
    const [scenicCorridorRoutesRaw, walkAdventureRoutesRaw, walkVarietyRoutes, walkParkRoutes, multiStopRoutes, calmCorridorRoutesAll, townsCorridorRoutes] = await discoveryAllPromise;
    if (isCancelled()) return;
    pipe(
      `DISCOVERY resolved — waited ${Math.round(performance.now() - discoveryT0)}ms ` +
      `beyond pass 1 (deadline ${DISCOVERY_DEADLINE_MS}ms from its arming)`,
    );
    // The variety walk + through-the-park walk share the walk-adventure budget
    // filter below (same "discovered nicer way on foot" intent), so combine first.
    const walkAdventureRoutesAll = [...walkAdventureRoutesRaw, ...walkVarietyRoutes, ...walkParkRoutes];
    // The calm-roads route is the near-direct MIDDLE GROUND — keep it only when
    // it stays within the Pleasant budget (it's not a "long way"). Folded into
    // extraRoutes so it competes for the Pleasant slot. It carries the
    // "calm-corridor" tag, which (like scenic-corridor) lets it survive the
    // coarse sig-dedup below even when its time/distance nearly matches Direct.
    const calmDirectDur = fastestDurForBudget || 0;
    const calmCorridorRoutes =
      calmDirectDur > 0
        ? calmCorridorRoutesAll.filter((r) => r.duration <= calmDirectDur + pleasantBudgetSec)
        : calmCorridorRoutesAll;
    if (calmCorridorRoutes.length) {
      extraRoutes.push(...calmCorridorRoutes);
      perf(
        `calm-roads folded in (${calmCorridorRoutes.length} of ${calmCorridorRoutesAll.length} within budget; durations ${calmCorridorRoutes
          .map((r) => Math.round(r.duration / 60) + "m")
          .join(", ")})`,
      );
    }
    // Length-scaled detour cap: drop a walk/bike adventure that runs longer than
    // the direct route by more than its budgeted multiple (a short walk may
    // wander a bigger multiple; a long one tapers). Keeps a 10-min walk from
    // surfacing a 40-min "adventure".
    let walkAdventureRoutes = walkAdventureRoutesAll;
    if (walkAdventureRoutesAll.length > 0) {
      const { walkAdventureBudget } = await import("@/utils/adventureBudget");
      const tripKm = (tripMilesStraight || 0) * 1.60934;
      const { maxMultiplier } = walkAdventureBudget(
        tripKm,
        effectiveMode === "biking" ? "biking" : "walking",
      );
      const directDur = fastestDurForBudget || 0;
      walkAdventureRoutes =
        directDur > 0
          ? walkAdventureRoutesAll.filter((r) => r.duration <= directDur * maxMultiplier)
          : walkAdventureRoutesAll;
    }
    // All are "discovered long way" candidates and share the scenic-corridor
    // tag + downstream handling (classifier surfaces them as the adventure card).
    // multiStopRoutes is the per-leg pleasant route for trips WITH stops.
    // Drop any multi-stop route that ZIGZAGS (high self-overlap). The per-leg
    // scenic-via insertion can crisscross in dense areas ("went nuts when I added
    // detours" — Morgan) — better to fall back to the clean route through the
    // user's stops than surface spaghetti.
    const cleanMultiStop = multiStopRoutes.filter((r) => selfOverlapFraction(r.coordinates) < 0.06);
    const scenicCorridorRoutes = [...scenicCorridorRoutesRaw, ...walkAdventureRoutes, ...cleanMultiStop, ...townsCorridorRoutes];
    if (isCancelled()) return;
    if (townsCorridorRoutes.length) {
      perf(
        `charming-towns route folded in (through ${(townsCorridorRoutes[0].townNames ?? []).join(", ")}; ${townsCorridorRoutes
          .map((r) => Math.round(r.duration / 60) + "m")
          .join(", ")})`,
      );
    }
    if (walkAdventureRoutes.length) {
      perf(
        `walk/bike adventure folded in (${walkAdventureRoutes.length} routes; durations ${walkAdventureRoutes
          .map((r) => Math.round(r.duration / 60) + "m")
          .join(", ")})`,
      );
    }
    // Log each candidate's duration so we can SEE whether it's a genuine long
    // scenic detour (e.g. the Maine coast at ~4h) or a near-duplicate of Direct.
    perf(
      `scenic corridor folded in (${scenicCorridorRoutes.length} routes` +
        (scenicCorridorRoutes.length
          ? `; durations ${scenicCorridorRoutes.map((r) => Math.round(r.duration / 60) + "m").join(", ")}`
          : "") +
        `)`,
    );

    // Everything discovery produced goes through the SAME admission the core
    // pool went through, into the same `raw` / `seenSig`.
    const discoveredAdmitted = admitToPool([...extraRoutes, ...scenicCorridorRoutes]);
    perf(
      `raw pool ${raw.length} routes (${raw.filter((r) => getRouteSource(r) === "scenic-corridor").length} scenic-corridor survived dedup)`,
    );
    // THE COASTAL ROUTE'S FATE, STATED EVERY RUN (Morgan, Aug 2). It kept
    // appearing in some runs and vanishing in others, and every trimmed log
    // ended just short of saying why. One unmissable line: either the coastal
    // candidate is in the pool with its duration and retrace meters, or it
    // isn't — and if it isn't, the dedupe/sig or an upstream drop already
    // happened and the "pool: dropped" line above names it.
    {
      const coastal = raw.filter((r) => getRouteSource(r) === "coastal-pch");
      console.log(
        coastal.length > 0
          ? `[coastal] POOL: present ×${coastal.length} — ${coastal
              .map((r) => `${Math.round(r.duration / 60)}m/retrace ${Math.round(contiguousRetraceMeters(r.coordinates))}m`)
              .join(", ")}`
          : `[coastal] POOL: ABSENT — injected but did not survive to the pool (see any "pool: dropped" line above)`,
      );
    }

    if (raw.length === 0) throw new Error("No routes found");

    // THE CHEAP RE-CLASSIFY IS NOW CONDITIONAL (Morgan, decision 1: "those
    // cards are authoritative and must never regress to cheap scoring").
    //
    // This pass exists to show the discovered routes quickly, before the slow
    // enriched classification lands. That was worth doing when the cards on
    // screen were themselves cheap. It is not worth doing once pass 1 has put
    // ENRICHED cards up: this would overwrite real OSM road classes, real
    // scenery-layer verdicts and real community tags with the flat baseline —
    // the exact substitution rule 20 is about. The discovered routes wait the
    // few extra seconds for pass 2 instead.
    if (authoritativeCardsEmitted) {
      perf(
        `cheap re-classify SKIPPED — ${discoveredAdmitted.length} discovered route(s) ` +
        `will wait for pass-2 enrichment rather than displace enriched cards`,
      );
    } else if (extraRoutes.length > 0 || valhallaRoutes.length > 0 || scenicCorridorRoutes.length > 0) {
      // (#129 stage 2) Three-cards-always for refresh path too.
      const refreshed = classifyThreeCards(cheapScore(raw), avoidHighways, {
        mode: effectiveMode,
        pleasantBudgetSec,
        pleasantTimeCapSec,
        googleCoords,
        // (#155) Refresh path runs AFTER the scenic-waypoint Mapbox calls
        // resolve — scenic anchors are now in the pool. If Scenic still
        // collapses here, it's because no real scenic alternative exists,
        // not because we haven't looked yet. Clear the "Searching scenic"
        // copy in favor of honest "Same as the Pleasant route" copy.
        scenicAnchorsLoaded: true,
        isLoop: dest?.name === "Pleasant Loop",
      });
      // Never fewer cards than the user can already see — see neverShrink.
      if (!isCancelled()) g.setScored((prev) => neverShrink(prev, refreshed));
      perf(`reclassified cards: ${refreshed.map((r) => r.classification).join(", ") || "(none)"}`);
    }


    // ================= PASS 2 — DISCOVERED ROUTES ONLY =================
    // Enrich only what discovery added. Every route pass 1 measured is reused
    // exactly as it was measured: nothing is fetched, tiled or scored twice.
    const pass1ByRoute = new Map(pass1Entries.map((e) => [e.route, e]));
    const newRoutes = raw.filter((r) => !pass1ByRoute.has(r));
    const fastestDur = Math.min(...raw.map((r) => r.duration));

    // DECISION 3 (Morgan, Aug 23): "If pass 2 discovers a genuinely faster
    // route, re-score. Do not reject it as 'implausible'. fastestDur affects
    // scoring, so if the minimum changes, confess it in diagnostics and
    // recompute the affected scoring against the new fastest duration.
    // Correctness beats assuming discovered routes can never be faster."
    //
    // fastestDuration feeds the time-cost term of every score, so a moved
    // minimum silently re-prices the whole pool. Re-scoring reuses the inputs
    // enrichPool already fetched — no network, no second enrichment.
    if (pass1Entries.length > 0 && fastestDur < fastestDurCore) {
      const winner = raw.reduce((a, b) => (b.duration < a.duration ? b : a));
      // eslint-disable-next-line no-console
      console.warn(
        `[fastest-changed] a discovered route is FASTER than anything the core ` +
        `pool held — ${getRouteSource(winner) ?? "engine"}/${Math.round(fastestDur / 60)}m ` +
        `beats ${Math.round(fastestDurCore / 60)}m. Re-scoring all ` +
        `${pass1Entries.length} pass-1 route(s) against the new minimum.`,
      );
      pass1Entries = rescoreForFastest(pass1Entries, fastestDur);
    }

    chooseEnrichTargets(newRoutes, fastestDur);
    const newDeep = newRoutes.filter((r) => deepEnrich.has(r));

    // DECISION 2 (Morgan): "Approve the second osmEnv fetch, but only for
    // genuinely new route geography... Do not blindly redo the full corridor
    // OSM fetch." fetchOsmEnvironmentForNewRoutes tiles the discovered routes
    // the same way the core fetch tiled the core ones, subtracts the cells
    // already queried, and asks Overpass only for the remainder — nothing at
    // all when the discovered routes stay inside the corridor we already have.
    //
    // NOT AWAITED HERE, for the reason earned on Aug 20 and written above the
    // core fetch: its only consumer is the final classify. Awaiting it now
    // would spend up to 8s of what is left of the ceiling BEFORE pass-2
    // enrichment issues a request — shortening the very budget that decides
    // whether the discovered routes get measured at all. Held as a promise and
    // awaited at the point of use, so the wait is max(top-up, pass 2) rather
    // than the sum.
    const corridorOsmEnvPromise: Promise<OsmEnvironment> = (async () => {
      const core = await coreCorridorOsmEnvPromise;
      if (enrichmentTooFar || newRoutes.length === 0) return core;
      const suppBudgetMs = Math.min(
        CORRIDOR_OSM_BUDGET_MS,
        Math.max(0, READY_CEILING_MS - (performance.now() - T0)),
      );
      if (suppBudgetMs <= 0) {
        perf("corridor OSM top-up SKIPPED — no budget left before the ceiling");
        return core;
      }
      const supp = await Promise.race([
        fetchOsmEnvironmentForNewRoutes(
          newRoutes.map((r) => r.coordinates),
          coreCorridorCoords,
        ).catch(() => null),
        new Promise<null>((res) => setTimeout(() => res(null), suppBudgetMs)),
      ]);
      if (!supp) {
        perf("corridor OSM top-up timed out — dead-end detection uses the core environment");
        return core;
      }
      if (supp.fetchedTiles === 0) {
        perf(
          `corridor OSM top-up: NO new ground — ${supp.reusedTiles} tile(s) ` +
          `already covered by the core fetch, no Overpass call made`,
        );
        return core;
      }
      perf(
        `corridor OSM top-up: ${supp.fetchedTiles} new tile(s) fetched, ` +
        `${supp.reusedTiles} reused from pass 1`,
      );
      return mergeOsmEnvironments(core, supp.env);
    })();

    // Whatever is left of the ceiling. NO 4s floor here, unlike pass 1: pass 1
    // is the pass whose failure means cheap cards and therefore always gets a
    // real attempt, but pass 2 is an upgrade on cards that are already good, so
    // it may not push past the ceiling to get one.
    const pass2BudgetMs = Math.max(0, READY_CEILING_MS - (performance.now() - T0));
    pipe(
      `PASS-2 ENRICHMENT start — new=${newRoutes.length} deep=${newDeep.length} ` +
      `budget=${Math.round(pass2BudgetMs)}ms`,
    );
    const pass2: EnrichPoolResult = newRoutes.length === 0
      ? { routes: [], ok: true, deepAttempted: 0, deepSettled: 0, ms: 0 }
      : await enrichPool(newRoutes, {
          fastestDuration: fastestDur,
          deepEnrich, placesEnrich,
          routePrefs,
          profile: effectiveProfile,
          prioritizeCommunity,
          toleranceMultiplier: tolerance,
          mode: effectiveMode,
          buffer,
          sampleCap: SAMPLE_CAP[effectiveMode],
          enclosingParksPromise,
          budgetMs: pass2BudgetMs,
          label: "pass2",
          isCancelled,
        });
    if (isCancelled()) return;
    pipe(
      `PASS-2 ENRICHMENT ${pass2.ok ? "OK" : "TIMED OUT"} in ${pass2.ms}ms — ` +
      `deep ${pass2.deepSettled}/${pass2.deepAttempted} settled`,
    );

    if (!pass2.ok) {
      // THE SUCCESS CONDITION Morgan asked to be able to prove: pass 2 did not
      // finish, and the cards the user is looking at are still the ENRICHED
      // pass-1 cards. No cheap re-classification is allowed to replace them on
      // the way out, so nothing is written here at all.
      discoveryComplete = true;
      g.setStatus("ready", null);
      _telemetryBudgetMs = pass2BudgetMs;
      // Pass 2 ran out of budget. Whether that is a degraded RESULT depends
      // entirely on whether pass 1 had already seated enriched cards.
      _telemetryVerdict = authoritativeCardsEmitted ? "enriched" : "degraded";
      if (authoritativeCardsEmitted) {
        pipe(
          "FINAL CARDS = the pass-1 AUTHORITATIVE cards — pass-2 enrichment ran " +
          "out of budget; discovered routes were not seated (no cheap fallback)",
        );
        perf("READY at ceiling — kept the enriched pass-1 cards");
      } else {
        // DEGRADED RESULT, SAID OUT LOUD (Morgan, Aug 23 — safety net A).
        // Nothing on screen ever became authoritative: pass 1 had no core pool
        // and pass 2 could not finish. The cards are cheap-scored.
        // eslint-disable-next-line no-console
        console.warn(
          `[degraded] Pleasant/Scenic cards are from CHEAP scoring — no enriched ` +
          `FeatureSummary ever became authoritative for this search. ` +
          `corridor=${dest?.name ?? "trip"} mode=${effectiveMode} ` +
          `at=${startCoords[1].toFixed(3)},${startCoords[0].toFixed(3)} ` +
          `pool=${raw.length} pass2BudgetMs=${Math.round(pass2BudgetMs)} ` +
          `elapsedMs=${Math.round(performance.now() - T0)}`,
        );
      }
      return;
    }

    // Union of both passes, in pool order so nothing downstream sees a
    // reshuffle it didn't ask for.
    const byRoute = new Map<RawRoute, EnrichedRoute>();
    for (const e of [...pass1Entries, ...pass2.routes]) byRoute.set(e.route, e);
    const enrichedEntries = raw
      .map((r) => byRoute.get(r))
      .filter((e): e is EnrichedRoute => !!e);
    const enriched = enrichedEntries.map((e) => e.scored);
    if (enriched.length === 0) {
      pipe("FINAL CLASSIFY SKIPPED — no scored routes; keeping what is on screen");
      discoveryComplete = true;
      g.setStatus("ready", null);
      return;
    }

    // PRODUCTION SHADOW (Morgan, Aug 22). This is the ONLY classification in
    // the app that runs on real FeatureSummary data - OSM road classes, the
    // scenery layer, community tags, the segment engine - so it is the only
    // place a shadow run can answer which layer is choosing badly in a REAL
    // search. Off unless the debug flag is set; when off the cost is one
    // localStorage read. Recording only: `classified` below is computed
    // identically either way and nothing in the shadow path can reach it.
    const __shadowOn = shadowEnabled();
    if (__shadowOn) {
      enableAttrition(true);
      clearTraces();
      beginAttrition(`${dest?.name ?? "trip"}`, enriched.length);
    }

    // (#129 stage 2) Three-cards-always for the enriched classification.
    const classified = classifyThreeCards(enriched, avoidHighwayRoutes, {
      excludeCoords: shownPleasantRef.current,
      skipRank: tryAgainOpts?.attempt ?? 0,
      mode: effectiveMode,
      isLoop: dest?.name === "Pleasant Loop",
      pleasantBudgetSec,
      pleasantTimeCapSec,
      googleCoords,
      // (#155) Final classification — enrichment is complete, scenic
      // anchors have been looked up. No more "Searching scenic" copy.
      scenicAnchorsLoaded: true,
      // (#171) Activate dead-end filter — pass corridor-wide OSM env.
      // hasDeadEndDetour reads opts.osmEnv.lines to find noexit=yes /
      // service=driveway ways. Without this the filter is dormant.
      // Awaited HERE, its only point of use — it has been fetching
      // alongside enrichment since well above, so by now it is almost
      // always already resolved.
      osmEnv: await corridorOsmEnvPromise,
    });

    if (__shadowOn) {
      try {
        const pl = classified.find((c) => c.classification === "pleasant");
        const sc = classified.find((c) => c.classification === "scenic");
        const trace = endAttrition(
          pl ? roadsOfSteps(pl.route.steps) : undefined,
          sc ? roadsOfSteps(sc.route.steps) : undefined,
        );
        const shortestM = enriched.length
          ? Math.min(...enriched.map((e) => e.route.distance))
          : 0;
        runProductionShadow({
          corridor: `${dest?.name ?? "trip"}`,
          km: shortestM / 1000,
          mode: effectiveMode,
          enriched,
          classified,
          trace,
          pleasantTimeCapSec,
        });
      } catch (err) {
        // A diagnostic must never be able to break a search.
        console.warn("[shadow] recording failed (cards unaffected):", err);
      } finally {
        enableAttrition(false);
      }
    }

    // TEMP DIAGNOSTIC (Morgan / White Plains): print every enriched candidate's
    // real signals + the final label so we can see WHY a downtown-threading
    // route wins — or whether a cleaner alternative even exists in the pool.
    try {
      const label = (e: typeof enriched[number]) =>
        classified.find((c) => c.route === e.route)?.classification ?? "-";
      // eslint-disable-next-line no-console
      console.log("[diag] " + enriched
        .slice()
        .sort((a, b) => (b.scores.pleasant ?? 0) - (a.scores.pleasant ?? 0))
        .map((e) => {
          const f = e.features;
          const com = (f?.cafes ?? 0) + (f?.shops ?? 0);
          return `${label(e)}:${getRouteSource(e.route)}/${Math.round(e.route.duration / 60)}m/hwy${Math.round(f?.highwayPct ?? 0)}/com${com}/pl${Math.round(e.scores.pleasant ?? 0)}`;
        })
        .join("  |  "));
    } catch { /* diagnostic only */ }
    const newPleasant = classified.find((c) => c.classification === "pleasant");
    if (newPleasant) shownPleasantRef.current.push(newPleasant.route.coordinates);
    const fastestRaw = enriched.reduce((a, b) =>
      a.route.duration <= b.route.duration ? a : b,
    );
    g.setFastestPleasantBaseline(fastestRaw.scores.pleasant);
    // Both flags now hold. `authoritativeCardsEmitted` may already have been
    // set by pass 1; `discoveryComplete` is set HERE and only here on the
    // success path — after it, a late-arriving discovery route can never be
    // enriched, so the late fold's cheap classification is the last word it
    // will get, and it may only ADD.
    authoritativeCardsEmitted = true;
    discoveryComplete = true;
    // THE RATCHET applies to the final classify too — this was the exact
    // pass that evicted Michigan's coastal-pch card at 24s (Aug 3 log).
    g.setScored((prev) => {
      const guarded = neverShrink(prev, classified);
      authoritativeClassified = guarded;
      return guarded;
    });
    authoritativeClassified ??= classified;
    // THE PROOF LINE, closing the chain that started at CORE ROUTES. Every
    // card names whether its own route went through deep enrichment.
    {
      const deepRoutes = new Set(enrichedEntries.filter((e) => e.deep).map((e) => e.route));
      pipe(
        "FINAL CARDS — " +
        (authoritativeClassified ?? []).map((c) =>
          `${c.classification}=${getRouteSource(c.route) ?? "engine"}/` +
          `${Math.round(c.route.duration / 60)}m/enriched=${deepRoutes.has(c.route) ? "yes" : "NO"}`,
        ).join(" "),
      );
      pipe(
        `SUMMARY core=${corePool.length} → pass1 deep=${pass1?.deepSettled ?? 0}/${pass1?.deepAttempted ?? 0} ` +
        `→ authoritative=${authoritativeCardsEmitted ? "yes" : "no"} ` +
        `→ discovery +${discoveredAdmitted.length} → pass2 deep=${pass2.deepSettled}/${pass2.deepAttempted} ` +
        `→ final pool=${enriched.length}`,
      );
    }

    // (#175) Build & emit diagnostics — what's in the pool, where it
    // came from, what won each slot. Picked up by Index.tsx and rendered
    // in the DebugOverlay on the route screen.
    if (g.setRouteDiagnostics) {
      const sourceCounts: Record<string, number> = {};
      for (const s of enriched) {
        const src = getRouteSource(s.route);
        sourceCounts[src] = (sourceCounts[src] ?? 0) + 1;
      }
      const slotSummary = (cls: "fastest" | "pleasant" | "scenic") => {
        const c = classified.find((x) => x.classification === cls);
        if (!c) return null;
        return {
          source: getRouteSource(c.route),
          distanceMi: c.route.distance / 1609.34,
          durationMin: c.route.duration / 60,
          highwayPct: c.features.highwayPct ?? 0,
          pleasantScore: c.scores.pleasant ?? 0,
          collapsed: c.sameAsFastest === true || c.sameAsClass != null,
        };
      };
      const diag: RouteDiagnostics = {
        poolSize: enriched.length,
        sourceCounts,
        direct: slotSummary("fastest"),
        pleasant: slotSummary("pleasant"),
        adventure: slotSummary("scenic"),
        builtAtMs: Date.now(),
      };
      g.setRouteDiagnostics(diag);
    }
    if (effectiveTolerance === "scenic") {
      const epicPleasant = classified.find((r) =>
        r.classification === "pleasant" && (r.route.epicHighwayMeters ?? 0) > 1000,
      );
      const longest = classified.reduce((a, b) => (a.route.duration >= b.route.duration ? a : b));
      g.setSelectedClass(epicPleasant?.classification ?? longest.classification);
    } else {
      g.setSelectedClass("pleasant");
    }
    // Enriched, classified, and selected — the request is fully fulfilled.
    g.setStatus("ready", null);
    _telemetryVerdict = "enriched";
    _telemetryBudgetMs = READY_CEILING_MS;
    perf("READY — Pleasant route fully resolved (total time)");

    // Community signals along surfaced routes.
    try {
      const routeSamples = classified
        .flatMap((r) => samplePolylineMax(r.route.coordinates, 24));
      const signals = await fetchSignalsAlongRoute(routeSamples, 150, 90);
      if (isCancelled()) return;
      g.setTags(signals.map((s) => ({
        id: s.id,
        user_id: "",
        lat: s.lat,
        lng: s.lng,
        travel_mode: effectiveMode,
        tag_label: s.tag_label,
        sentiment: s.sentiment,
        created_at: s.created_at,
        pleasantness: s.pleasantness,
        report_kind: s.report_kind,
        reporter_weight: s.reporter_weight,
      })));
    } catch { /* ignore */ }
  } catch (e: unknown) {
    if (isCancelled()) return;
    const raw = e instanceof Error ? e.message : String(e);
    // Translate common low-level errors into something a tester can act on.
    // The raw network/Mapbox messages ("Failed to fetch", "401",
    // "NoRoute", etc.) tell you what but not what to do. Real-drive UX
    // audit (#56): the "Could not fetch routes" toast surfaced
    // implementation noise instead of guidance.
    let friendly = "Couldn't load routes. Try again in a moment.";
    if (/offline|networkerror|failed to fetch|load failed|networkrequestfailed/i.test(raw)) {
      // Morgan's wording (7/11 real-drive report): dead zones and one-bar
      // cell service land here — "offline" reads wrong when the phone shows
      // bars, so say what's actually needed.
      friendly = "You need a stronger network to find a pleasant route. Try again when you have more signal.";
    } else if (/noroute|no route|cannot find a route|no_route|noroutefound/i.test(raw)) {
      friendly =
        effectiveMode === "walking"
          ? "No walking route found for a trip this long. Try Drive or Bike."
          : effectiveMode === "biking"
            ? "No bike route found for a trip this long. Try Drive."
            // NEVER "no route between those two points" (Morgan, Aug 8, after
            // San Jose → Flagstaff — a real 720-mile interstate drive — said
            // exactly that). Two towns on a continent nearly always CAN be
            // driven between; when we come back empty it is our problem, not
            // the map's, and telling the driver to pick a different
            // destination is both wrong and insulting. Say something is
            // missing on our side and give them the one action that actually
            // helps: re-ground the start.
            : "Something's missing on our end. Try reloading your starting point to ground the map in the right place, then search again.";
    } else if (/profilenotfound|invalid profile|invalid request/i.test(raw)) {
      friendly = "Routing service didn't understand that trip. Try a nearby address.";
    } else if (/401|403|unauthorized|forbidden/i.test(raw)) {
      friendly = "Routing service is unavailable right now. Please try again soon.";
    } else if (/429|too many requests|rate.?limit/i.test(raw)) {
      friendly = "We've hit our routing limit for the moment. Try again in a minute.";
    } else if (/timeout|timed out/i.test(raw)) {
      // Weak-signal twin of the offline case above — a request that starts
      // but never finishes is one bar of service, not a server problem.
      friendly = "You need a stronger network to find a pleasant route. Try again when you have more signal.";
    }
    g.setStatus("error", friendly);
    toast.error(friendly);
  } finally {
    // Disarm, always. A stale deadline left armed would make the NEXT caller —
    // possibly one with a full 23s to spend — bypass the edge function for a
    // ceiling that expired long ago.
    setOverpassDeadline(null);
    // One row per search, always, whatever exit was taken — including the error
    // path, which stays "unmeasured" because nothing was ever measured. Cell
    // counts come from the delta in the Overpass layer's own counters over this
    // window. Fire-and-forget: recordSearch never throws and never awaits.
    recordSearch({
      handle: _telemetry,
      budgetMs: _telemetryBudgetMs,
      elapsedMs: performance.now() - T0,
      verdict: _telemetryVerdict,
      routeDistanceM: _telemetryRouteM,
      mode: effectiveMode,
      lat: startCoords?.[1],
      lng: startCoords?.[0],
    });
    // Never leave the gate closed: the ceiling path returns before the release
    // above, and a permanently-gated discovery promise would also hang the late
    // fold that awaits it — killing the safety net on exactly the searches that
    // need it most. Resolving twice is a no-op.
    releaseDiscovery();
    if (!isCancelled()) g.setLoading(false);
  }
}
